// 🔥 Hot Reload Test - This comment was added to test live reload functionality
import { useEffect, useState, useCallback, useRef, KeyboardEvent, MouseEvent, ReactElement, startTransition } from "react";
import { Routes, Route, useNavigate, useParams, useLocation, Navigate, Link } from "react-router-dom";
import { signOut, onAuthStateChanged, User, getRedirectResult, signInAnonymously } from "firebase/auth";
import { auth } from "./firebase";
import { doc, getDoc, updateDoc, setDoc, query, collection, where, getDocs, addDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { Workspace } from "./types";
import { api } from "./services/api";

import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import CreateWorkspace from "./pages/CreateWorkspace";
import AdminPage from "./pages/AdminPage";
import Activity from "./pages/Activity";
import Accounts from "./pages/Accounts";
import MobileAccounts from "./pages/MobileAccounts";
import Analytics from "./pages/Analytics";
import MobileAnalytics from "./pages/MobileAnalytics";
import CategoryGroupDetail from "./pages/CategoryGroupDetail";
import Settings from "./pages/Settings";
import InviteAcceptance from "./pages/InviteAcceptance";
import CreateTransactionMobile from "./pages/CreateTransactionMobile";
import MobileAccountForm from "./pages/MobileAccountForm";
import LoadingSpinner from "./components/LoadingSpinner";
import DevWorkspaceIndicator from "./components/DevWorkspaceIndicator";
import TermsAcceptanceDialog from "./components/TermsAcceptanceDialog";
import { WorkspaceDataPreloader, ActivityDataPreloader, AccountsDataPreloader, SettingsDataPreloader } from "./components/WorkspaceDataPreloader";
import { ThemeSync } from "./contexts/ThemeContext";
import logo from "./assets/BudgetTrace Logomark_V2.svg";
import { WORKSPACE_SWITCHING_CONFIG } from "./config/ui";
import { workspaceNameToSlug, defaultTransactionIdPrefixFromName } from "./utils/slug";
import { isDemoMode, enableDemoMode, disableDemoMode, getDemoWorkspaceId, clearDemoCache } from "./utils/demoMode";
import { DemoModeMessage } from "./utils/demoModeMessage";
import { isBypassAuthEnabled, getBypassWorkspaceId, setBypassWorkspaceId, getDefaultDevWorkspaceName } from "./utils/bypassAuth";
import { getDevModeType, setDevModeType } from "./utils/devModeType";
import { ISO4217_CURRENCIES } from "./utils/iso4217Currencies";
import { isActualMobileDevice } from "./utils/deviceDetection";
// Import cleanup functions to expose them to window in development
import "./utils/cleanupTransactions";
import "./utils/cleanupDevUser";
import "./utils/fixWorkspaceOwner";
import {
  saveWorkspaceContext,
  getWorkspaceContext,
  clearWorkspaceContext,
  hasWorkspaceContext,
  broadcastToTabs,
  listenToTabEvents,
  isWorkspaceContextValid,
  getTabSessionId,
  isTabLoggedOut,
  clearTabLoggedOutFlag,
  type TabEvent,
} from "./utils/tabContext";

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Paper,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Autocomplete,
  Snackbar,
  Alert,
  Tooltip,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import LogoutIcon from "@mui/icons-material/Logout";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";

const COMMON_CURRENCIES: readonly { code: string; name: string; symbol: string }[] = ISO4217_CURRENCIES.map((c) => ({
  code: c.code,
  name: c.name,
  symbol: c.symbol,
}));

/* Redirect to login once on mount when actually logged out. If Firebase has a user but App state hasn't updated yet, do nothing to avoid redirect loop. */
function RedirectToLogin(): ReactElement {
  const navigate = useNavigate();
  useEffect(() => {
    // Only redirect when there is no Firebase user. If there is a user, we're in an inconsistent state
    // (e.g. isProcessingRedirectResult caused auth handler to skip), so don't navigate and avoid loop.
    if (!auth.currentUser) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
      <CircularProgress />
    </Box>
  );
}

/* -------------------- */
/* Initial App Loader   */
/* -------------------- */
function InitialLoader(): ReactElement {
  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="background.default"
    >
      <Paper
        elevation={0}
        sx={{ width: 360, p: 4, borderRadius: 3, textAlign: "center" }}
      >
          <Stack spacing={3} alignItems="center">
            <Stack spacing={0.25} alignItems="center">
              <Box
                component="img"
                src={logo}
                alt="Budget Trace"
                sx={{
                  width: 96,
                  height: "auto",
                }}
              />
              <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                Budget Trace
              </Typography>
            </Stack>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Loading app...
            </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

type PageType = "expenses" | "analytics" | "accounts" | "reports" | "settings";

/* -------------------- */
/* Route Wrappers       */
/* -------------------- */
function InviteAcceptanceWrapper(): ReactElement {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  if (!invitationId) {
    return <Navigate to="/" replace />;
  }

  // Validate invitationId - reject common page names and short strings that are clearly not invitation IDs
  // Invitation IDs are typically UUIDs or long random strings, not simple words like "activity", "analytics", etc.
  const reservedPageNames = ["activity", "analytics", "accounts", "reports", "settings", "login", "create-workspace", "create-account", "edit-account", "create-transaction", "edit-transaction", "transaction"];
  const isReservedPageName = reservedPageNames.includes(invitationId.toLowerCase());
  const isTooShort = invitationId.length < 8; // Invitation IDs should be at least 8 characters
  
  if (isReservedPageName || isTooShort) {
    // This is clearly not an invitation ID - redirect to home
    return <Navigate to="/" replace />;
  }

  return (
    <InviteAcceptance
      invitationId={invitationId}
      onAccept={async (workspaceId) => {
          try {
          // Get workspace name to build URL
          // Retry if workspace read fails (might need a moment for permissions to propagate)
          let wsSnap = await getDoc(doc(db, "workspaces", workspaceId));
          if (!wsSnap.exists()) {
            // Retry once immediately
            wsSnap = await getDoc(doc(db, "workspaces", workspaceId));
          }
          
          if (wsSnap.exists()) {
            const wsData = wsSnap.data();
            const wsName = wsData?.name ?? "workspace";
            const slug = workspaceNameToSlug(wsName);
            
            // Update user's active workspace if available
            const user = auth.currentUser;
            if (user) {
              try {
                await updateDoc(doc(db, "users", user.uid), {
                  activeWorkspaceId: workspaceId,
                });
                
                // Save workspace context for this tab
                saveWorkspaceContext({
                  workspaceId: workspaceId,
                  workspaceName: wsName,
                  workspaceLogoUrl: wsData?.logoUrl as string | undefined,
                  userId: user.uid,
                  timestamp: Date.now(),
                });
              } catch (err) {
                console.error("Error updating active workspace:", err);
                // Continue anyway - the member document is already created
              }
            }
            
            // Use full page reload to ensure App.tsx fetches the workspace list
            window.location.href = `/${slug}/activity`;
          } else {
            // Still redirect - App.tsx will handle it or show create workspace
            navigate("/");
          }
        } catch (err) {
          console.error("Error navigating after invitation acceptance:", err);
          // Still try to navigate - the member document was created, so it should work
          navigate("/");
        }
      }}
    />
  );
}

/* -------------------- */
/* Main App Component   */
/* -------------------- */
function App(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | undefined>(undefined);
  const [checking, setChecking] = useState<boolean>(true);
  // Initialize sidebar state from localStorage to persist across page refreshes
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    const savedState = localStorage.getItem("sidebarOpen");
    return savedState === "true";
  });
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState<HTMLElement | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState<string>("");
  const [newWorkspaceCurrency, setNewWorkspaceCurrency] = useState<typeof COMMON_CURRENCIES[number] | null>(null);
  const [creatingWorkspace, setCreatingWorkspace] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<PageType>("expenses");
  const [switchingWorkspace, setSwitchingWorkspace] = useState<boolean>(false);
  const [switchingFromWorkspace, setSwitchingFromWorkspace] = useState<string | null>(null);
  const [switchingToWorkspace, setSwitchingToWorkspace] = useState<string | null>(null);
  const [activityInitialFilters, setActivityInitialFilters] = useState<{
    category?: string;
    account?: string;
    users?: string[];
    startDate?: string;
    endDate?: string;
    typeFilter?: string;
  } | undefined>(undefined);
  const [showLoginPage, setShowLoginPage] = useState<boolean>(false);
  const [showTermsDialog, setShowTermsDialog] = useState<boolean>(false);
  const [pendingUserCreation, setPendingUserCreation] = useState<User | null>(null);
  const termsDialogShownRef = useRef<boolean>(false); // Prevent showing terms dialog multiple times
  const [analyticsKey, setAnalyticsKey] = useState<number>(0);
  
  // Snackbar for demo mode alerts
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarElement, setSnackbarElement] = useState<ReactElement | null>(null);
  
  // Track if we've already navigated to prevent multiple navigations (use ref to persist across rapid auth changes)
  const hasNavigatedRef = useRef<boolean>(false);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track pending navigation to prevent duplicate navigation attempts
  const pendingNavigationRef = useRef<string | null>(null);
  // Track last navigation path to prevent loops
  const lastNavigationPathRef = useRef<string | null>(null);
  // Track last navigation time to prevent rapid-fire navigations
  const lastNavigationTimeRef = useRef<number>(0);
  // Track current pathname in a ref to avoid stale closures
  const currentPathRef = useRef<string>(location.pathname);
  const locationPathnameRef = useRef<string>(location.pathname);
  
  // Update ref when location changes (without triggering useEffect)
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);
  // Navigation cooldown period (ms) - prevent navigation within this time
  // Increased to 3 seconds for production to prevent throttling errors in Chrome/Opera
  const NAVIGATION_COOLDOWN_MS = 3000;
  
  // Track if we're manually switching workspace to prevent auth handler from interfering
  const isManuallySwitchingWorkspace = useRef<boolean>(false);
  
  // Track if we've completed the initial auth check (to prevent loader on page navigation)
  const hasCompletedInitialAuthCheck = useRef<boolean>(false);
  
  // Track if auth processing is in progress (use ref to persist across renders)
  const isProcessingAuthRef = useRef<boolean>(false);
  
  // Track if we're processing a redirect result to prevent navigation during redirect handling
  const isProcessingRedirectResult = useRef<boolean>(false);
  // When user opens /admin while logged out, we send them to login; after login send them back to /admin
  const redirectAfterLoginRef = useRef<string | null>(null);
  // Ensure post-redirect navigation runs only once (avoids multiple setTimeouts from effect re-runs overwriting /admin)
  const hasRunPostRedirectNavigationRef = useRef<boolean>(false);
  const postRedirectTimeoutIdRef = useRef<number | null>(null);
  const hasCheckedRedirectResultRef = useRef<boolean>(false);

  // Update current path ref when location changes
  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  // Persist sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem("sidebarOpen", String(sidebarOpen));
  }, [sidebarOpen]);

  // Helper function to safely navigate with debouncing and flooding protection
  const safeNavigate = useCallback((path: string, delay: number = 0): void => {
    // Don't navigate if we're processing redirect result (wait for it to complete)
    if (isProcessingRedirectResult.current) {
      return;
    }
    
    const now = Date.now();
    const currentPath = currentPathRef.current; // Use ref to get latest path
    
    // Don't navigate if already navigating to the same path
    if (pendingNavigationRef.current === path) {
      return;
    }
    
    // Don't navigate if we're already on the target path
    if (currentPath === path) {
      return;
    }
    
    // Don't navigate if we just navigated to this path (prevent loops)
    if (lastNavigationPathRef.current === path) {
      const timeSinceLastNav = now - lastNavigationTimeRef.current;
      if (timeSinceLastNav < NAVIGATION_COOLDOWN_MS) {
        return;
      }
    }
    
    // Don't navigate if we navigated very recently (cooldown period)
    const timeSinceLastNav = now - lastNavigationTimeRef.current;
    if (timeSinceLastNav < NAVIGATION_COOLDOWN_MS && lastNavigationPathRef.current !== null) {
      return;
    }
    
    // Clear any existing navigation timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
    
    // Set pending navigation flag
    pendingNavigationRef.current = path;
    hasNavigatedRef.current = true;
    lastNavigationPathRef.current = path;
    lastNavigationTimeRef.current = now;
    
    // Schedule navigation
    navigationTimeoutRef.current = setTimeout(() => {
      // Double-check we're not already on the path before navigating
      const latestPath = currentPathRef.current;
      if (latestPath !== path) {
        navigate(path, { replace: true });
      }
      navigationTimeoutRef.current = null;
      pendingNavigationRef.current = null;
    }, delay);
  }, [navigate]);

  const handleDemoClick = useCallback(async () => {
    try {
      const demoWorkspaceId = getDemoWorkspaceId();
      
      // Enable demo mode (stores in sessionStorage)
      enableDemoMode();
      
      // Fetch workspace data (now possible with public read access)
      const wsSnap = await getDoc(doc(db, "workspaces", demoWorkspaceId));
      if (!wsSnap.exists()) {
        console.error("Demo workspace not found");
        return;
      }
      
      const wsData = wsSnap.data();
      const wsName = wsData?.name ?? "Demo Workspace";
      
      // Set workspace state for demo mode
      setWorkspaceId(demoWorkspaceId);
      setWorkspaceName(wsName);
      setWorkspaceLogoUrl(wsData?.logoUrl as string | undefined);
      
      // Navigate to the workspace
      const slug = workspaceNameToSlug(wsName);
      navigate(`/${slug}/activity`);
    } catch (error) {
      console.error("Error loading demo workspace:", error);
      // Still enable demo mode even if there's an error
      enableDemoMode();
      const demoWorkspaceId = getDemoWorkspaceId();
      setWorkspaceId(demoWorkspaceId);
      setWorkspaceName("Demo Workspace");
      navigate(`/demo-workspace/activity`);
    }
  }, [navigate]);

  const handleLogout = async (): Promise<void> => {
    // Get current workspace context before clearing
    const currentContext = getWorkspaceContext();
    
    // Broadcast logout event to other tabs
    if (currentContext) {
      broadcastToTabs({
        type: "logout",
        tabSessionId: getTabSessionId(),
        workspaceId: currentContext.workspaceId,
        userId: currentContext.userId,
      });
    }
    
    // Clear workspace context for this tab
    clearWorkspaceContext();
    
    // Clear demo mode if active
    if (isDemoMode()) {
      disableDemoMode();
    }
    
    // Global logout - sign out from Firebase (affects all tabs)
    // This will trigger onAuthStateChanged in all tabs, which will clear their state
    await signOut(auth);
    
    setAuthUser(null);
    setWorkspaceId(null);
    setWorkspaceName(null);
    setWorkspaceLogoUrl(undefined);
    setWorkspaces([]);
    navigate("/");
  };

  // Handle terms acceptance
  const handleTermsAccept = async (): Promise<void> => {
    if (!pendingUserCreation) return;

    try {
      const user = pendingUserCreation;
      const isTestUser = user.uid.startsWith("test-user-");

      if (isTestUser) {
        // For test users, just show success message and close dialog
        console.log("✅ [TEST MODE] Terms accepted for test user:", user.uid);
        setShowTermsDialog(false);
        setPendingUserCreation(null);
        alert("✅ [TEST MODE] Terms dialog test successful! In production, this would create/update the user document.");
        return;
      }

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        // Create new user document with terms acceptance
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          name: user.displayName,
          photoURL: user.photoURL,
          activeWorkspaceId: null,
          termsAccepted: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
        console.log("User document created with terms acceptance");
      } else {
        // Update existing user document with terms acceptance
        await updateDoc(userRef, {
          termsAccepted: serverTimestamp(),
        });
        console.log("User document updated with terms acceptance");
      }

      setShowTermsDialog(false);
      setPendingUserCreation(null);
      termsDialogShownRef.current = false; // Reset ref when dialog closes

      // Continue with workspace loading after terms are accepted
      // Fetch workspaces and navigate appropriately
      const workspacesList = await fetchUserWorkspaces(user.uid);
      
      if (workspacesList.length > 0) {
        // User has workspaces - navigate to first one or active one
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.data();
        const activeWorkspaceId = userData?.activeWorkspaceId as string | undefined;
        
        let workspaceToUse = workspacesList.find(ws => ws.id === activeWorkspaceId) || workspacesList[0];
        
        if (workspaceToUse) {
          const slug = workspaceNameToSlug(workspaceToUse.name);
          navigate(`/${slug}/activity`, { replace: true });
        }
      } else {
        // No workspaces - navigate to create workspace
        navigate("/create-workspace", { replace: true });
      }
    } catch (err) {
      console.error("Error accepting terms:", err);
      alert("Failed to save terms acceptance. Please try again.");
    }
  };

  // Handle terms decline
  const handleTermsDecline = async (): Promise<void> => {
    // If user declines terms, sign them out
    await signOut(auth);
    setShowTermsDialog(false);
    setPendingUserCreation(null);
    termsDialogShownRef.current = false; // Reset ref
    navigate("/");
  };

  // Dev-only: Test terms dialog with mock user
  const handleTestTermsDialog = useCallback((): void => {
    if (process.env.NODE_ENV === "production") {
      console.warn("Terms dialog test is only available in development mode");
      return;
    }

    // Create a mock user object for testing
    const mockUser = {
      uid: "test-user-" + Date.now(),
      email: "test@example.com",
      displayName: "Test User",
      photoURL: null,
      emailVerified: true,
      isAnonymous: false,
      phoneNumber: null,
      providerId: "firebase",
      metadata: {
        creationTime: new Date().toISOString(),
        lastSignInTime: new Date().toISOString(),
      },
      providerData: [],
      refreshToken: "",
      tenantId: null,
      delete: async () => {},
      getIdToken: async () => "",
      getIdTokenResult: async () => ({} as any),
      reload: async () => {},
      toJSON: () => ({}),
    } as unknown as User;

    setPendingUserCreation(mockUser);
    setShowTermsDialog(true);
  }, []);

  // Helper function to ensure user is in workspace members subcollection
  const ensureWorkspaceMembership = useCallback(async (workspaceId: string, userId: string): Promise<void> => {
    try {
      // Check if user is already a member
      const memberRef = doc(db, "workspaces", workspaceId, "members", userId);
      const memberSnap = await getDoc(memberRef);
      
      if (memberSnap.exists()) {
        // User is already a member, nothing to do
        return;
      }

      // Check if user is the workspace owner
      const workspaceRef = doc(db, "workspaces", workspaceId);
      const workspaceSnap = await getDoc(workspaceRef);
      
      if (!workspaceSnap.exists()) {
        console.error("Workspace does not exist:", workspaceId);
        return;
      }

      const workspaceData = workspaceSnap.data();
      const isOwner = workspaceData?.ownerId === userId;

      if (isOwner) {
        // Get current user info
        const currentUser = auth.currentUser;
        // Add owner to members subcollection
        await setDoc(memberRef, {
          uid: userId,
          displayName: currentUser?.displayName || null,
          email: currentUser?.email || null,
          role: "owner",
          joinedAt: serverTimestamp(),
        });
        console.log("Added workspace owner to members subcollection");
      } else {
        // User is not owner and not a member - this shouldn't happen for normal flow
        // but we'll log it for debugging
        console.warn("User is not owner and not a member of workspace:", workspaceId);
      }
    } catch (err) {
      console.error("Error ensuring workspace membership:", err);
    }
  }, []);

  const fetchUserWorkspaces = useCallback(async (userId: string): Promise<Workspace[]> => {
    try {
      // Use backend API to fetch all workspaces where user is a member (owner or invited)
      // This includes workspaces where user is owner AND workspaces where user was invited
      // Added timeout handling - if API is slow, this won't block UI
      const workspacesList = await api.getWorkspaces();

      setWorkspaces(workspacesList);
      return workspacesList;
    } catch (err) {
      console.error("Error fetching workspaces:", err);
      // Fallback to empty array if API fails - don't block app initialization
      // This allows app to continue with cached context even if API is slow
      setWorkspaces([]);
      return [];
    }
  }, []);

  const handleWorkspaceSelect = async (selectedWorkspaceId: string): Promise<void> => {
    if (selectedWorkspaceId === workspaceId) {
      setWorkspaceMenuAnchor(null);
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) return;

      // Mark that we're manually switching workspace
      isManuallySwitchingWorkspace.current = true;

      // Ensure user is in members subcollection before switching
      await ensureWorkspaceMembership(selectedWorkspaceId, user.uid);

      // Get the new workspace name and logo before switching
      const newWsSnap = await getDoc(doc(db, "workspaces", selectedWorkspaceId));
      const newWsData = newWsSnap.data();
      const newWorkspaceName = newWsData?.name ?? "Workspace";
      const newWorkspaceLogoUrl = newWsData?.logoUrl as string | undefined;

      // Set loading state with transition info
      setSwitchingFromWorkspace(workspaceName);
      setSwitchingToWorkspace(newWorkspaceName);
      setSwitchingWorkspace(true);
      setWorkspaceMenuAnchor(null);

      // Update workspace state immediately (before Firestore update) to prevent refresh
      setWorkspaceId(selectedWorkspaceId);
      setWorkspaceName(newWorkspaceName);
      setWorkspaceLogoUrl(newWorkspaceLogoUrl);
      
      // Save workspace context for this tab immediately
      if (user) {
        saveWorkspaceContext({
          workspaceId: selectedWorkspaceId,
          workspaceName: newWorkspaceName,
          workspaceLogoUrl: newWorkspaceLogoUrl,
          userId: user.uid,
          timestamp: Date.now(),
        });
      }

      // Update Firestore in background (don't wait for it)
      updateDoc(doc(db, "users", user.uid), {
        activeWorkspaceId: selectedWorkspaceId,
      }).catch((err) => {
        console.error("Error updating activeWorkspaceId:", err);
      });

      // Small delay to show the transition screen
      await new Promise(resolve => setTimeout(resolve, WORKSPACE_SWITCHING_CONFIG.SWITCHING_DELAY_MS));

      setSwitchingWorkspace(false);
      setSwitchingFromWorkspace(null);
      setSwitchingToWorkspace(null);
      
      // Navigate to the new workspace URL using replace to avoid history issues
      const slug = workspaceNameToSlug(newWorkspaceName);
      navigate(`/${slug}/activity`, { replace: true });
      
      isManuallySwitchingWorkspace.current = false;
    } catch (err) {
      console.error("Error switching workspace:", err);
      setSwitchingWorkspace(false);
      setSwitchingFromWorkspace(null);
      setSwitchingToWorkspace(null);
      isManuallySwitchingWorkspace.current = false;
    }
  };

  // Fetch exchange rates from API
  const fetchExchangeRatesFromAPI = async (baseCurrency: string, targetCurrencies: string[]): Promise<Record<string, number | null>> => {
    try {
      // Use exchangerate-api.com free endpoint (no API key required)
      const response = await fetch(`https://api.exchangerate-api.com/v4/latest/${baseCurrency}`);
      
      if (!response.ok) {
        console.error("Failed to fetch exchange rates from API");
        return {};
      }

      const data = await response.json();
      const rates: Record<string, number | null> = {};

      // Extract rates for target currencies
      targetCurrencies.forEach((currencyCode) => {
        if (currencyCode === baseCurrency) {
          rates[currencyCode] = 1.0;
        } else if (data.rates && data.rates[currencyCode]) {
          rates[currencyCode] = data.rates[currencyCode] as number;
        } else {
          rates[currencyCode] = null; // Rate not available
        }
      });

      return rates;
    } catch (error) {
      console.error("Error fetching exchange rates from API:", error);
      return {};
    }
  };

  const handleCreateWorkspace = async (): Promise<void> => {
    if (!newWorkspaceName.trim() || !newWorkspaceCurrency) return;

    // In demo mode, workspace cannot be created
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Workspaces cannot be created. Please log in to create a workspace." />
      );
      setSnackbarOpen(true);
      setCreateDialogOpen(false);
      setNewWorkspaceName("");
      setNewWorkspaceCurrency(null);
      return;
    }

    setCreatingWorkspace(true);
    // Close menu if open
    setWorkspaceMenuAnchor(null);
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Use backend API to create workspace (this will automatically copy account groups from Reference Workspace)
      const workspace = await api.createWorkspace(newWorkspaceName.trim(), newWorkspaceCurrency.code);

      // Set default transaction ID prefix in settings so it appears in Settings > Workspace (works in deployed and local)
      const defaultPrefix = defaultTransactionIdPrefixFromName(newWorkspaceName.trim());
      if (defaultPrefix) {
        try {
          const settingsRef = doc(db, "workspaces", workspace.id, "settings", "general");
          await setDoc(settingsRef, { transactionIdPrefix: defaultPrefix }, { merge: true });
        } catch (prefixErr) {
          console.warn("Could not set initial transaction ID prefix:", prefixErr);
        }
      }

      // Update workspace state
      setWorkspaceId(workspace.id);
      setWorkspaceName(newWorkspaceName);
      setWorkspaceLogoUrl(undefined); // New workspace has no logo initially
      
      // Save workspace context for this tab
      saveWorkspaceContext({
        workspaceId: workspace.id,
        workspaceName: newWorkspaceName,
        workspaceLogoUrl: undefined,
        userId: user.uid,
        timestamp: Date.now(),
      });
      
      setNewWorkspaceName("");
      setNewWorkspaceCurrency(null);
      setCreateDialogOpen(false);
      
      // Refresh workspace list
      await fetchUserWorkspaces(user.uid);
      
      // Navigate to the new workspace
      const slug = workspaceNameToSlug(newWorkspaceName);
      navigate(`/${slug}/activity`);
    } catch (err) {
      console.error("Workspace creation failed:", err);
    } finally {
      setCreatingWorkspace(false);
    }
  };

  // Handle redirect result from Firebase Auth (run at most once per app load to avoid loop and log spam)
  useEffect(() => {
    const handleRedirectResult = async (): Promise<void> => {
      if (hasCheckedRedirectResultRef.current) {
        return;
      }
      hasCheckedRedirectResultRef.current = true;
      // Mark that we're processing redirect to prevent navigation during this time
      isProcessingRedirectResult.current = true;
      
      try {
        console.log("Checking for redirect result...");
        console.log("Current URL:", window.location.href);
        console.log("Current origin:", window.location.origin);
        
        // Check URL params for redirect indicators
        const urlParams = new URLSearchParams(window.location.search);
        const hasAuthParams = urlParams.has('apiKey') || urlParams.has('authType');
        console.log("URL has auth params:", hasAuthParams, urlParams.toString());
        
        const result = await getRedirectResult(auth);
        console.log("Redirect result:", result ? "Success" : "No redirect pending", result?.user?.email);
        
        if (result && result.user) {
          const user = result.user;
          console.log("User authenticated:", user.email);
          
          // Check if user document exists and if terms have been accepted
          const userRef = doc(db, "users", user.uid);
          const snap = await getDoc(userRef);
          
          if (!snap.exists()) {
            // New user - show terms dialog before creating user document
            setPendingUserCreation(user);
            setShowTermsDialog(true);
          } else {
            // Existing user - check if terms were accepted
            const userData = snap.data();
            if (!userData?.termsAccepted) {
              // User exists but hasn't accepted terms - show dialog
              setPendingUserCreation(user);
              setShowTermsDialog(true);
            }
          }
        } else {
          console.log("No redirect result - user may have cancelled or no pending redirect");
          if (hasAuthParams) {
            console.warn("⚠️ URL has auth params but no redirect result - possible redirect URL mismatch!");
            console.warn("Expected domain: budgettrace.io, Current origin:", window.location.origin);
          }
        }
      } catch (err) {
        // Log all redirect errors for debugging
        console.error("Error handling redirect result:", err);
        if (err instanceof Error) {
          console.error("Error message:", err.message);
          console.error("Error code:", (err as any).code);
        }
      } finally {
        // Wait a bit before allowing navigation (let auth state settle)
        const timeoutId = window.setTimeout(() => {
          isProcessingRedirectResult.current = false;
          // Run post-redirect navigation at most once (effect can run multiple times → multiple 1500ms timeouts)
          if (hasRunPostRedirectNavigationRef.current) {
            return;
          }
          console.log("Redirect processing flag cleared, isProcessingRedirectResult:", isProcessingRedirectResult.current);
          
          // After redirect processing completes, check if we need to navigate
          // This handles the case where onAuthStateChanged returned early during redirect processing
          const currentUser = auth.currentUser;
          const currentPath = window.location.pathname;
          console.log("Post-redirect check - Current user:", currentUser?.email || "none", "Current path:", currentPath);
          
          if (currentUser) {
            // If user came from /admin and was sent to login, take them to /admin now (only if not already there)
            if (redirectAfterLoginRef.current === "/admin") {
              hasRunPostRedirectNavigationRef.current = true;
              redirectAfterLoginRef.current = null;
              if (currentPath !== "/admin") {
                console.log("✅ Post-redirect: taking user to /admin");
                navigate("/admin", { replace: true });
              }
              return;
            }
            // If we're on "/", "/login", or malformed "/login/activity" (etc.) after redirect login, trigger navigation
            const isOnLoginOrRoot = currentPath === "/" || currentPath === "/login";
            const loginSubpathMatch = currentPath.match(/^\/login\/(activity|analytics|accounts|reports|settings)(\/.*)?$/);
            if (isOnLoginOrRoot || (loginSubpathMatch && getWorkspaceContext())) {
              hasRunPostRedirectNavigationRef.current = true;
              if (loginSubpathMatch) {
                console.log("✅ On malformed /login/<page> after redirect, fixing URL...");
              } else {
                console.log("✅ On login/root page after redirect, triggering navigation...");
              }
              
              // Use a small delay to ensure auth state is fully settled
              setTimeout(async () => {
                try {
                  console.log("🔄 Starting post-redirect navigation logic...");
                  
                  // Fetch user workspaces and active workspace
                  console.log("Fetching user workspaces and user document...");
                  const [workspacesList, userSnap] = await Promise.allSettled([
                    fetchUserWorkspaces(currentUser.uid),
                    getDoc(doc(db, "users", currentUser.uid)),
                  ]);
                  
                  const workspaces = workspacesList.status === 'fulfilled' ? workspacesList.value : [];
                  const userDoc = userSnap.status === 'fulfilled' ? userSnap.value : null;
                  const activeWorkspaceId = userDoc?.data()?.activeWorkspaceId as string | null | undefined ?? null;
                  
                  console.log("Workspace data:", {
                    workspacesCount: workspaces.length,
                    activeWorkspaceId,
                    workspacesListStatus: workspacesList.status,
                    userDocStatus: userSnap.status
                  });
                  
                  // Check saved context first
                  const savedContext = getWorkspaceContext();
                  const hasValidSavedContext = savedContext && 
                                             isWorkspaceContextValid(savedContext, currentUser.uid) &&
                                             savedContext.userId === currentUser.uid;
                  
                  console.log("Saved context check:", {
                    hasSavedContext: !!savedContext,
                    hasValidSavedContext,
                    savedContextUserId: savedContext?.userId,
                    currentUserId: currentUser.uid
                  });
                  
                  let workspaceIdToUse: string | null = null;
                  let workspaceNameToUse: string | null = null;
                  
                  if (hasValidSavedContext) {
                    workspaceIdToUse = savedContext.workspaceId;
                    workspaceNameToUse = savedContext.workspaceName;
                    console.log("Using saved context:", { workspaceIdToUse, workspaceNameToUse });
                  } else if (activeWorkspaceId) {
                    workspaceIdToUse = activeWorkspaceId;
                    // Fetch workspace name
                    try {
                      const wsSnap = await getDoc(doc(db, "workspaces", activeWorkspaceId));
                      if (wsSnap.exists()) {
                        workspaceNameToUse = wsSnap.data()?.name ?? "Workspace";
                        console.log("Using active workspace:", { workspaceIdToUse, workspaceNameToUse });
                      } else {
                        console.warn("Active workspace ID exists but workspace document not found:", activeWorkspaceId);
                      }
                    } catch (err) {
                      console.error("Error fetching workspace:", err);
                    }
                  } else if (workspaces.length > 0) {
                    workspaceIdToUse = workspaces[0].id;
                    workspaceNameToUse = workspaces[0].name;
                    console.log("Using first workspace from list:", { workspaceIdToUse, workspaceNameToUse });
                  } else {
                    console.log("No workspace found - user has no workspaces");
                  }
                  
                  const finalPath = window.location.pathname;
                  console.log("Final path check before navigation:", finalPath);
                  const finalLoginSubpath = finalPath.match(/^\/login\/(activity|analytics|accounts|reports|settings)(\/.*)?$/);
                  const shouldFixPath = finalPath === "/" || finalPath === "/login" || finalLoginSubpath;
                  
                  if (workspaceIdToUse && workspaceNameToUse) {
                    const slug = workspaceNameToSlug(workspaceNameToUse);
                    const targetPath = finalLoginSubpath
                      ? `/${slug}/${finalLoginSubpath[1]}${finalLoginSubpath[2] || ""}`
                      : `/${slug}/activity`;
                    
                    if (shouldFixPath) {
                      console.log("🚀 Navigating to workspace after redirect:", targetPath);
                      navigate(targetPath, { replace: true });
                    } else {
                      console.log("⚠️ Not navigating - already on different path:", finalPath);
                    }
                  } else {
                    if (shouldFixPath) {
                      console.log("🚀 No workspace found, navigating to create workspace");
                      navigate("/create-workspace", { replace: true });
                    } else {
                      console.log("⚠️ Not navigating to create workspace - already on different path:", finalPath);
                    }
                  }
                } catch (err) {
                  console.error("❌ Error during post-redirect navigation:", err);
                  if (err instanceof Error) {
                    console.error("Error details:", err.message, err.stack);
                  }
                }
              }, 500); // Small delay to ensure everything is settled
            } else {
              console.log("Not on login/root page, skipping post-redirect navigation. Current path:", currentPath);
            }
          } else {
            console.log("No current user after redirect processing, skipping navigation");
          }
        }, 1500); // 1.5 second delay to let auth state change handler settle
        postRedirectTimeoutIdRef.current = timeoutId;
      }
    };
    
    handleRedirectResult();
    return () => {
      if (postRedirectTimeoutIdRef.current !== null) {
        clearTimeout(postRedirectTimeoutIdRef.current);
        postRedirectTimeoutIdRef.current = null;
      }
    };
  }, [navigate, fetchUserWorkspaces]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      console.log("[onAuthStateChanged] Fired", {
        hasUser: !!user,
        userEmail: user?.email || "none",
        currentPath: location.pathname,
        isProcessingAuth: isProcessingAuthRef.current,
        isProcessingRedirect: isProcessingRedirectResult.current
      });
      
      // Prevent multiple concurrent auth state processing using ref (persists across renders)
      if (isProcessingAuthRef.current) {
        console.log("[onAuthStateChanged] Skipping - already processing auth");
        return;
      }
      
      console.log("[onAuthStateChanged] Processing auth state, user:", !!user);
      
      // If user wanted to go to /admin after login (came from /admin -> login), send them there now
      if (user && redirectAfterLoginRef.current === "/admin") {
        redirectAfterLoginRef.current = null;
        setChecking(false);
        isProcessingAuthRef.current = false;
        hasCompletedInitialAuthCheck.current = true;
        if (locationPathnameRef.current !== "/admin") {
          navigate("/admin", { replace: true });
        }
        return;
      }
      
      // Skip navigation if we're processing redirect result (let it settle first)
      // BUT: Don't skip if we're already on a workspace page during hard refresh
      // OR if we're on "/" or "/login" (we need to navigate after redirect login)
      // OR if bypass auth is enabled (bypass auth doesn't depend on redirect results)
      // (redirect processing shouldn't block workspace loading on workspace pages or bypass auth)
      // Use ref to avoid dependency on location.pathname which causes infinite loops
      const currentPath = locationPathnameRef.current;
      const isOnWorkspaceRoutePatternRedirect = currentPath.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|edit-transaction|create-transaction|create-account|edit-account|transaction)/);
      const isOnLoginOrRoot = currentPath === "/" || currentPath === "/login";
      const isBypassMode = isBypassAuthEnabled();
      const shouldSkipForRedirect = isProcessingRedirectResult.current && user && !isOnWorkspaceRoutePatternRedirect && !isOnLoginOrRoot && !isBypassMode;
      
      console.log("[onAuthStateChanged] Redirect check", {
        isOnWorkspaceRoutePattern: !!isOnWorkspaceRoutePatternRedirect,
        isOnLoginOrRoot,
        isBypassMode,
        shouldSkipForRedirect
      });
      
      if (shouldSkipForRedirect) {
        // Set user and checking so we render authenticated routes (e.g. /admin); don't navigate yet - wait for redirect processing
        console.log("[onAuthStateChanged] Skipping navigation - waiting for redirect processing to complete");
        setAuthUser(user);
        setChecking(false);
        return;
      }
      
      // Skip processing if we're manually switching workspace
      if (isManuallySwitchingWorkspace.current && user) {
        // Just ensure checking is false and return early
        setChecking(false);
        return;
      }
      
      // If we've completed initial auth check and we're already on a workspace page,
      // don't show loader during navigation between pages
      // Use ref to avoid dependency on location.pathname
      const currentPathForHandler = locationPathnameRef.current;
      const isOnWorkspacePage = currentPathForHandler.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|edit-transaction|create-transaction|create-account|edit-account|transaction)/);
      const shouldShowLoader = !hasCompletedInitialAuthCheck.current || !user || !isOnWorkspacePage;
      
      isProcessingAuthRef.current = true;
      if (shouldShowLoader) {
        setChecking(true);
      } else {
        // If we shouldn't show loader (already on workspace page), ensure checking is false
        setChecking(false);
      }
      
      // Clear any pending navigation timeout to prevent navigation flooding
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      // Clear pending navigation flag
      pendingNavigationRef.current = null;
      
      // Reset navigation flag only if we're not already on a valid route
      // This prevents unnecessary resets during rapid auth changes
      const isOnValidRoute = currentPathForHandler === "/" || 
                             currentPathForHandler === "/login" || 
                             currentPathForHandler.startsWith("/invite/") ||
                             currentPathForHandler === "/create-workspace" ||
                             currentPathForHandler === "/admin" ||
                             isOnWorkspacePage;
      if (!isOnValidRoute) {
        hasNavigatedRef.current = false;
      }

      if (!user) {
        console.log("[Auth] No user, checking for demo/bypass mode", {
          isBypassEnabled: isBypassAuthEnabled(),
          bypassWorkspaceId: getBypassWorkspaceId()
        });
        // Check for demo mode
        console.log("[Auth] Checking demo mode:", isDemoMode());
        if (isDemoMode()) {
          console.log("[Auth] Demo mode is active, loading demo workspace");
          const demoWorkspaceId = getDemoWorkspaceId();
          try {
            const wsSnap = await getDoc(doc(db, "workspaces", demoWorkspaceId));
            if (wsSnap.exists()) {
              const wsData = wsSnap.data();
              const wsName = wsData?.name ?? "Demo Workspace";
              console.log("[Auth] Demo workspace loaded, returning early");
              setWorkspaceId(demoWorkspaceId);
              setWorkspaceName(wsName);
              setWorkspaceLogoUrl(wsData?.logoUrl as string | undefined);
              setWorkspaces([]);
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
          } catch (error) {
            console.error("Error loading demo workspace:", error);
            disableDemoMode();
          }
        } else {
          console.log("[Auth] Demo mode is NOT active, continuing to bypass check");
        }
        
        console.log("[Auth] After demo check, checking bypass auth...");
        // Check for bypass auth mode (development only)
        if (isBypassAuthEnabled()) {
          console.log("[Auth] Bypass auth IS enabled, entering bypass block");
          
          // Sign in with anonymous user to enable Firestore writes
          // Firestore client SDK requires authentication for writes, even if rules allow unauthenticated access
          // The rules will still allow writes to dev workspace via isDevWorkspace check
          if (!auth.currentUser) {
            try {
              console.log("[Bypass Auth] Signing in with anonymous user for Firestore writes...");
              const userCredential = await signInAnonymously(auth);
              console.log("[Bypass Auth] Successfully signed in with anonymous user:", userCredential.user.uid);
            } catch (anonError: any) {
              const errorCode = anonError?.code || "unknown";
              const errorMessage = anonError?.message || "Unknown error";
              
              if (errorCode === "auth/admin-restricted-operation") {
                console.error("⚠️ [Bypass Auth] Anonymous authentication is not enabled in Firebase Console!");
                console.error("📋 To enable anonymous auth:");
                console.error("   1. Go to: https://console.firebase.google.com/project/budget-trace/authentication/providers");
                console.error("   2. Find 'Anonymous' in the list of sign-in providers");
                console.error("   3. Click on 'Anonymous' → Click 'Enable' → Click 'Save'");
                console.error("   4. Restart your dev server (stop and run 'npm start' again)");
                console.error("   5. Transaction creation will work after enabling anonymous auth");
                console.error("");
                console.error("   Without anonymous auth, you can still view data but cannot create/edit transactions.");
                console.error("   Direct link: https://console.firebase.google.com/project/budget-trace/authentication/providers");
              } else {
                console.error("[Bypass Auth] Failed to sign in with anonymous user:", errorCode, errorMessage);
              }
              // Continue anyway - reads might still work, but writes will fail
              // Exit early - when anonymous sign-in completes, handler will fire again with user
              setChecking(false);
              isProcessingAuthRef.current = false;
              return;
            }
            // Anonymous sign-in succeeded - handler will fire again with the user
            // Exit here and let the next handler call (with user) load the workspace
            console.log("[Bypass Auth] Anonymous sign-in initiated, waiting for auth state change...");
            setChecking(false);
            isProcessingAuthRef.current = false;
            return;
          } else {
            console.log("[Bypass Auth] User already authenticated:", auth.currentUser.uid);
            // User exists - continue to load workspace below (this will happen in the next handler call with user)
            // For now, just exit and wait for handler to fire with the user
            setChecking(false);
            isProcessingAuthRef.current = false;
            return;
          }
        }
        
        // If we're on a workspace page, don't clear context immediately
        // Firebase auth might still be initializing after hard refresh
        // Check if we have saved context or are on a workspace route pattern
        // Use ref to avoid dependency on location.pathname
        const currentPathForLogout = locationPathnameRef.current;
        const isOnWorkspaceRoutePattern = currentPathForLogout.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|edit-transaction|create-transaction|create-account|edit-account)/);
        const savedContext = getWorkspaceContext();
        
        // If we're on a workspace page and have saved context, preserve context during auth initialization
        // This prevents clearing context during hard refresh before Firebase auth is ready
        const isOnWorkspaceRoutePatternPreserve = currentPathForLogout.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|edit-transaction|create-transaction|create-account|edit-account|transaction)/);
        if (isOnWorkspaceRoutePatternPreserve && savedContext && !hasCompletedInitialAuthCheck.current) {
          // Don't clear context yet - preserve it during hard refresh
          // Keep checking=true to show loader until auth resolves - context will be restored when user auth resolves
          // Don't set checking to false yet - we need to show loader during hard refresh
          isProcessingAuthRef.current = false;
          return;
        }
        
        // User logged out - clear workspace context for this tab
        // (Global logout clears all tabs via Firebase auth state change)
        // BUT: Only clear if we're not on a workspace page during initial auth check (hard refresh)
        const isOnWorkspaceRoutePatternLogout = currentPathForLogout.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|transaction)/);
        const savedContextLogout = getWorkspaceContext();
        const shouldPreserveContext = isOnWorkspaceRoutePatternLogout && savedContextLogout && !hasCompletedInitialAuthCheck.current;
        
        if (!shouldPreserveContext) {
          clearWorkspaceContext();
        }
        
        setAuthUser(null);
        setWorkspaceId(null);
        setWorkspaceName(null);
        setWorkspaces([]);
        setShowLoginPage(false);
        setChecking(false);
        isProcessingAuthRef.current = false;
        hasCompletedInitialAuthCheck.current = true;
        return;
      }

      setAuthUser(user);
      
      // Clear logged out flag if tab was previously logged out
      clearTabLoggedOutFlag();

      // If user was sent to login from /admin, take them to /admin after login instead of workspace
      if (redirectAfterLoginRef.current === "/admin") {
        redirectAfterLoginRef.current = null;
        setChecking(false);
        isProcessingAuthRef.current = false;
        hasCompletedInitialAuthCheck.current = true;
        if (locationPathnameRef.current !== "/admin") {
          navigate("/admin", { replace: true });
        }
        return;
      }

      // Check if user is anonymous (bypass auth mode)
      // Anonymous users don't have email and should use bypass auth logic
      const isAnonymousUser = !user.email && isBypassAuthEnabled();
      
      // Check if user has accepted terms (skip for anonymous users)
      // Terms are only shown once per user - if termsAccepted exists, skip the dialog
      if (!isAnonymousUser) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        
        // Only show terms dialog if user document doesn't exist OR termsAccepted is missing
        // This ensures terms are only shown once - after acceptance, termsAccepted is set and won't show again
        if (!userSnap.exists() || !userSnap.data()?.termsAccepted) {
          // User hasn't accepted terms - show dialog
          // Only set if not already set to prevent infinite loops
          if (!showTermsDialog && !termsDialogShownRef.current) {
            termsDialogShownRef.current = true;
            setPendingUserCreation(user);
            setShowTermsDialog(true);
          }
          // Don't proceed with workspace loading until terms are accepted
          setChecking(false);
          isProcessingAuthRef.current = false;
          return;
        }
        // Terms already accepted - reset the ref and continue
        termsDialogShownRef.current = false;
      }
      
      if (isAnonymousUser) {
        console.log("[Bypass Auth] Anonymous user detected, loading dev workspace...");
        let bypassWorkspaceId = getBypassWorkspaceId();
        console.log("[Bypass Auth] Got workspace ID:", bypassWorkspaceId);
        
        // Try to find "Dev_Workspace" by name first
        const targetWorkspaceName = getDefaultDevWorkspaceName();
        console.log("[Bypass Auth] Searching for workspace named:", targetWorkspaceName);
        
        try {
          // Try to query for workspace by name
          const workspacesRef = collection(db, "workspaces");
          const q = query(workspacesRef, where("name", "==", targetWorkspaceName));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            // Found workspace with target name
            const foundWorkspace = querySnapshot.docs[0];
            const foundId = foundWorkspace.id;
            const foundData = foundWorkspace.data();
            console.log("[Bypass Auth] Found workspace by name:", targetWorkspaceName, "ID:", foundId);
            
            // Update to use the found workspace
            bypassWorkspaceId = foundId;
            setBypassWorkspaceId(foundId);
            
            setWorkspaceId(foundId);
            setWorkspaceName(foundData?.name ?? targetWorkspaceName);
            setWorkspaceLogoUrl(foundData?.logoUrl as string | undefined);
            setWorkspaces([]);
            setChecking(false);
            isProcessingAuthRef.current = false;
            hasCompletedInitialAuthCheck.current = true;
            
            // Navigate to workspace activity page
            const slug = workspaceNameToSlug(foundData?.name ?? targetWorkspaceName);
            const currentPathForBypass1 = locationPathnameRef.current;
            const isOnWorkspacePage = currentPathForBypass1.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|edit-transaction|create-transaction|create-account|edit-account|transaction)/);
            if (!isOnWorkspacePage) {
              console.log("[Bypass Auth] Navigating to workspace:", `/${slug}/activity`);
              window.location.href = `/${slug}/activity`;
              return;
            }
            console.log("[Bypass Auth] Already on workspace page, workspace loaded");
            return;
          } else {
            console.log("[Bypass Auth] No workspace found with name:", targetWorkspaceName, "- using fallback ID");
          }
        } catch (queryError) {
          // Query might fail - that's okay, fall back to ID-based lookup
          console.log("[Bypass Auth] Could not query by name, using ID-based lookup:", queryError);
        }
        
        // Fall back to loading by ID
        if (bypassWorkspaceId) {
          try {
            console.log("[Bypass Auth] Loading workspace by ID:", bypassWorkspaceId);
            const wsSnap = await getDoc(doc(db, "workspaces", bypassWorkspaceId));
            if (wsSnap.exists()) {
              const wsData = wsSnap.data();
              const wsName = wsData?.name ?? "Workspace";
              console.log("[Bypass Auth] Workspace loaded:", wsName);
              setWorkspaceId(bypassWorkspaceId);
              setWorkspaceName(wsName);
              setWorkspaceLogoUrl(wsData?.logoUrl as string | undefined);
              setWorkspaces([]);
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              
              // Navigate to workspace activity page if not already on a workspace page
              const slug = workspaceNameToSlug(wsName);
              const currentPathForBypass2 = locationPathnameRef.current;
              const isOnWorkspacePage = currentPathForBypass2.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|edit-transaction|create-transaction|create-account|edit-account|transaction)/);
              if (!isOnWorkspacePage) {
                console.log("[Bypass Auth] Navigating to workspace:", `/${slug}/activity`);
                window.location.href = `/${slug}/activity`;
                return;
              }
              console.log("[Bypass Auth] Already on workspace page, workspace loaded");
              return;
            } else {
              console.warn("[Bypass Auth] Workspace not found:", bypassWorkspaceId);
            }
          } catch (error) {
            console.error("[Bypass Auth] Error loading workspace:", error);
          }
        }
        // If workspace not found, fall through to show create-workspace (but with bypass mode message)
        console.log("[Bypass Auth] Workspace not found, showing create workspace page");
      }

      // Check if this tab has a saved workspace context (for independent tab sessions)
      // Check this FIRST to potentially use cached context and avoid unnecessary API calls
      // Use ref to avoid dependency on location.pathname
      const currentPathForRestore = locationPathnameRef.current;
      const savedContext = getWorkspaceContext();
      const isOnWorkspaceRoutePatternForRestore = currentPathForRestore.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|transaction)/);
      
      // During hard refresh, be more lenient with saved context validation
      // If we're on a workspace page and have saved context, try to use it even if validation is strict
      const hasValidSavedContext = savedContext && 
                                   isWorkspaceContextValid(savedContext, user.uid) &&
                                   savedContext.userId === user.uid;
      
      const hasSavedContextDuringHardRefresh = savedContext && 
                                               savedContext.userId === user.uid &&
                                               isOnWorkspaceRoutePatternForRestore &&
                                               !hasCompletedInitialAuthCheck.current;
      
      console.log("[Hard Refresh Debug]", {
        hasSavedContext: !!savedContext,
        savedContextUserId: savedContext?.userId,
        currentUserId: user.uid,
        userIdMatch: savedContext?.userId === user.uid,
        isOnWorkspaceRoutePattern: !!isOnWorkspaceRoutePatternForRestore,
        hasCompletedInitialAuthCheck: hasCompletedInitialAuthCheck.current,
        hasValidSavedContext,
        hasSavedContextDuringHardRefresh,
        locationPathname: currentPathForRestore
      });
      
      // If we have valid saved context and are already on the workspace page, use it immediately
      // OR if we have saved context during hard refresh (be more lenient)
      if (hasValidSavedContext || hasSavedContextDuringHardRefresh) {
        // Use savedContext from outer scope (might be from hasSavedContextDuringHardRefresh check)
        const contextToUse = savedContext; // savedContext exists if we're in this block
        
        const slug = workspaceNameToSlug(contextToUse.workspaceName);
        const currentPathForSavedContext2 = locationPathnameRef.current;
        const isAlreadyOnWorkspace = currentPathForSavedContext2.startsWith(`/${slug}`);
        
        // During hard refresh on workspace page, restore from saved context even if slug doesn't match exactly
        // This handles cases where workspace name changed or slug format changed
        const isOnWorkspaceRoutePattern = currentPathForSavedContext2.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|transaction)/);
        const shouldRestoreFromSavedContext = isAlreadyOnWorkspace || (isOnWorkspaceRoutePattern && !hasCompletedInitialAuthCheck.current);
        
        if (shouldRestoreFromSavedContext && contextToUse) {
          console.log("[Hard Refresh Debug] Restoring workspace from saved context", {
            workspaceId: contextToUse.workspaceId,
            workspaceName: contextToUse.workspaceName,
            slug,
            isAlreadyOnWorkspace,
            shouldRestoreFromSavedContext
          });
          
          // Use cached context immediately - don't wait for API calls
          setWorkspaceId(contextToUse.workspaceId);
          setWorkspaceName(contextToUse.workspaceName);
          setWorkspaceLogoUrl(contextToUse.workspaceLogoUrl);
          setChecking(false);
          isProcessingAuthRef.current = false;
          hasCompletedInitialAuthCheck.current = true;
          
          // Fetch data in background (non-blocking)
          Promise.all([
            fetchUserWorkspaces(user.uid),
            getDoc(doc(db, "users", user.uid)),
          ]).then((results) => {
            const workspacesList = results[0];
            const userSnap = results[1];
            setWorkspaces(workspacesList);
            // Verify workspace membership in background (non-blocking)
            ensureWorkspaceMembership(contextToUse.workspaceId, user.uid).catch(err => {
              console.error("Background workspace membership check failed:", err);
            });
          }).catch(err => {
            console.error("Background data fetch failed:", err);
          });
          
          // If slug doesn't match but we're on workspace page pattern, navigate to correct slug
          // But only if we're not during hard refresh (avoid unnecessary navigation)
          if (!isAlreadyOnWorkspace && isOnWorkspaceRoutePattern && hasCompletedInitialAuthCheck.current) {
            safeNavigate(`/${slug}/activity`);
          }
          
          return; // Exit early - we're already using cached context
        }
      }

      // If on admin page, skip all workspace loading and redirect logic
      // Use window.location so we always have current URL (auth callback can have stale closure)
      if (typeof window !== "undefined" && window.location.pathname === "/admin") {
        setChecking(false);
        isProcessingAuthRef.current = false;
        hasCompletedInitialAuthCheck.current = true;
        return;
      }

      // Parallelize workspace fetch and user doc fetch (faster than sequential)
      // Use Promise.allSettled to prevent one slow call from blocking the other
      const [workspacesResult, userDocResult] = await Promise.allSettled([
        fetchUserWorkspaces(user.uid),
        getDoc(doc(db, "users", user.uid)),
      ]);
      
      // Extract results (handle failures gracefully)
      const userWorkspaces = workspacesResult.status === 'fulfilled' ? workspacesResult.value : [];
      const userSnap = userDocResult.status === 'fulfilled' ? userDocResult.value : null;
      
      // If user doc fetch failed, try to continue with saved context or empty
      if (!userSnap || !userSnap.exists()) {
        console.warn("User document fetch failed or doesn't exist, continuing with saved context or workspaces");
      }
      
      const wid = userSnap?.data()?.activeWorkspaceId as string | null | undefined ?? null;

      // If tab has a saved context and it's different from global activeWorkspaceId,
      // use the saved context (for independent workspace selection per tab)
      if (hasValidSavedContext && savedContext.workspaceId !== wid) {
        try {
          // Verify workspace still exists and user has access
          const wsSnap = await getDoc(doc(db, "workspaces", savedContext.workspaceId));
          if (wsSnap.exists()) {
            // Ensure user is in members subcollection (non-blocking - fire and forget)
            ensureWorkspaceMembership(savedContext.workspaceId, user.uid).catch(err => {
              console.error("Error ensuring workspace membership:", err);
            });
            
            // Restore workspace state from saved context
            setWorkspaceId(savedContext.workspaceId);
            setWorkspaceName(savedContext.workspaceName);
            setWorkspaceLogoUrl(savedContext.workspaceLogoUrl);
            
            // Don't redirect if user is on an invitation link or admin page
            const currentPathForInviteCheck = locationPathnameRef.current;
            if (currentPathForInviteCheck.startsWith("/invite/")) {
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
            if (currentPathForInviteCheck === "/admin") {
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
            
            // Check if we're already on the correct workspace path
            const slug = workspaceNameToSlug(savedContext.workspaceName);
            const currentPathForSavedContext = locationPathnameRef.current;
            const isAlreadyOnWorkspace = currentPathForSavedContext.startsWith(`/${slug}`);
            
            // If on /login after successful auth, always navigate to workspace
            const isOnLoginPage = currentPathForSavedContext === "/login";
            
            // Only navigate if not already on the workspace and haven't navigated recently
            // Always navigate if on login page (popup login success case)
            if (!isAlreadyOnWorkspace || isOnLoginPage) {
              // If on login page, navigate immediately without delay to avoid user seeing login page
              if (isOnLoginPage) {
                // Clear navigation flags to ensure navigation happens
                pendingNavigationRef.current = null;
                hasNavigatedRef.current = false;
                lastNavigationPathRef.current = null;
                navigate(`/${slug}/activity`, { replace: true });
              } else {
                safeNavigate(`/${slug}/activity`);
              }
            }
            
            // If already on workspace, set checking to false immediately (no delay needed)
            if (isAlreadyOnWorkspace && !isOnLoginPage) {
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
            
            setChecking(false);
            isProcessingAuthRef.current = false;
            hasCompletedInitialAuthCheck.current = true;
            return;
          } else {
            // Workspace no longer exists, clear context and fall through to use global activeWorkspaceId
            clearWorkspaceContext();
          }
        } catch (error) {
          console.error("Error restoring workspace context:", error);
          // Clear invalid context and fall through to use global activeWorkspaceId
          clearWorkspaceContext();
        }
      }
      
      // If saved context exists but matches global activeWorkspaceId, clear it to use global
      // (This ensures fresh logins from other devices work correctly)
      if (hasValidSavedContext && savedContext.workspaceId === wid) {
        // Context matches global - we'll use global, but keep context for future independent switching
        // No need to clear it
      }

      if (wid) {
        try {
          // Check if workspace exists first
          const wsSnap = await getDoc(doc(db, "workspaces", wid));
          if (!wsSnap.exists()) {
            // Workspace doesn't exist (was deleted), clear activeWorkspaceId
            await updateDoc(doc(db, "users", user.uid), {
              activeWorkspaceId: null,
            });
            setWorkspaceId(null);
            setWorkspaceName(null);
            setWorkspaceLogoUrl(undefined);
            // Don't redirect if user is on invitation link or admin page
            if (location.pathname !== "/create-workspace" && location.pathname !== "/admin" && !location.pathname.startsWith("/invite/")) {
              safeNavigate("/create-workspace");
            }
            setChecking(false);
            isProcessingAuthRef.current = false;
            hasCompletedInitialAuthCheck.current = true;
          } else {
            // Workspace exists, ensure user is in members subcollection (non-blocking)
            ensureWorkspaceMembership(wid, user.uid).catch(err => {
              console.error("Error ensuring workspace membership:", err);
            });
            
            const wsData = wsSnap.data();
            const wsName = wsData?.name ?? "Workspace";
            setWorkspaceName(wsName);
            setWorkspaceLogoUrl(wsData?.logoUrl as string | undefined);
            setWorkspaceId(wid);
            
            // Save workspace context for this tab
            saveWorkspaceContext({
              workspaceId: wid,
              workspaceName: wsName,
              workspaceLogoUrl: wsData?.logoUrl as string | undefined,
              userId: user.uid,
              timestamp: Date.now(),
            });
            
            // Don't redirect if user is on an invitation link or admin page
            if (location.pathname.startsWith("/invite/")) {
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
            if (location.pathname === "/admin") {
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
            
            // Check if we're already on the correct workspace path
            const slug = workspaceNameToSlug(wsName);
            const currentPathForWorkspace = locationPathnameRef.current;
            const isAlreadyOnWorkspace = currentPathForWorkspace.startsWith(`/${slug}`);
            
            // If on /login after successful auth, always navigate to workspace
            const isOnLoginPage = currentPathForWorkspace === "/login";
            
            // Only navigate if not already on the workspace and haven't navigated recently
            // Always navigate if on login page (popup login success case)
            if (!isAlreadyOnWorkspace || isOnLoginPage) {
              // If on login page, navigate immediately without delay to avoid user seeing login page
              if (isOnLoginPage) {
                // Clear navigation flags to ensure navigation happens
                pendingNavigationRef.current = null;
                hasNavigatedRef.current = false;
                lastNavigationPathRef.current = null;
                navigate(`/${slug}/activity`, { replace: true });
              } else {
                safeNavigate(`/${slug}/activity`);
              }
            }
            
            // If already on workspace, set checking to false immediately (no delay needed)
            if (isAlreadyOnWorkspace && !isOnLoginPage) {
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
            
            setChecking(false);
            isProcessingAuthRef.current = false;
            hasCompletedInitialAuthCheck.current = true;
          }
        } catch (error: any) {
          // Handle permission errors (workspace was deleted or user doesn't have access)
          console.error("Error loading workspace:", error);
          // Clear activeWorkspaceId if there's any error accessing the workspace
          try {
            await updateDoc(doc(db, "users", user.uid), {
              activeWorkspaceId: null,
            });
          } catch (updateError) {
            console.error("Error clearing activeWorkspaceId:", updateError);
          }
          setWorkspaceId(null);
          setWorkspaceName(null);
          setWorkspaceLogoUrl(undefined);
          // Don't redirect if user is on invitation link or admin page
          if (location.pathname !== "/create-workspace" && location.pathname !== "/admin" && !location.pathname.startsWith("/invite/")) {
            safeNavigate("/create-workspace", 150);
          }
          setChecking(false);
          isProcessingAuthRef.current = false;
          hasCompletedInitialAuthCheck.current = true;
        }
      } else {
        // No active workspace - check if user has workspaces
        if (userWorkspaces.length > 0) {
          // User has workspaces but no activeWorkspaceId - set the first one as active
          const firstWorkspace = userWorkspaces[0];
          try {
            // Update active workspace and set state in parallel (non-blocking)
            updateDoc(doc(db, "users", user.uid), {
              activeWorkspaceId: firstWorkspace.id,
            }).catch(err => {
              console.error("Error updating activeWorkspaceId:", err);
            });
            
            // Ensure user is in members subcollection (non-blocking)
            ensureWorkspaceMembership(firstWorkspace.id, user.uid).catch(err => {
              console.error("Error ensuring workspace membership:", err);
            });
            
            const wsName = firstWorkspace.name;
            setWorkspaceName(wsName);
            setWorkspaceLogoUrl(firstWorkspace.logoUrl);
            setWorkspaceId(firstWorkspace.id);
            
            // Save workspace context for this tab
            saveWorkspaceContext({
              workspaceId: firstWorkspace.id,
              workspaceName: wsName,
              workspaceLogoUrl: firstWorkspace.logoUrl,
              userId: user.uid,
              timestamp: Date.now(),
            });
            
            // Don't redirect if user is on an invitation link or admin page
            if (location.pathname.startsWith("/invite/")) {
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
            if (location.pathname === "/admin") {
              setChecking(false);
              isProcessingAuthRef.current = false;
              hasCompletedInitialAuthCheck.current = true;
              return;
            }
            
            // Navigate to workspace if not already there and haven't navigated recently
            // Always navigate if on login page (popup login success case)
            const slug = workspaceNameToSlug(wsName);
            const isOnLoginPage = location.pathname === "/login";
            const isAlreadyOnWorkspace = location.pathname.startsWith(`/${slug}`);
            if ((!hasNavigatedRef.current && !isAlreadyOnWorkspace) || isOnLoginPage) {
              hasNavigatedRef.current = true;
              // If on login page, navigate immediately without delay
              if (isOnLoginPage) {
                navigate(`/${slug}/activity`, { replace: true });
              } else {
                navigationTimeoutRef.current = setTimeout(() => {
                  navigate(`/${slug}/activity`, { replace: true });
                  navigationTimeoutRef.current = null;
                }, 100);
              }
            }
            
            setChecking(false);
            isProcessingAuthRef.current = false;
            hasCompletedInitialAuthCheck.current = true;
          } catch (error: any) {
            console.error("Error setting first workspace as active:", error);
            // If error, fall back to create workspace
            setWorkspaceId(null);
            setWorkspaceName(null);
            setWorkspaceLogoUrl(undefined);
            // Don't redirect if user is on invitation link or admin page
            if (location.pathname !== "/create-workspace" && location.pathname !== "/admin" && !location.pathname.startsWith("/invite/")) {
              safeNavigate("/create-workspace");
            }
            setChecking(false);
            isProcessingAuthRef.current = false;
            hasCompletedInitialAuthCheck.current = true;
          }
        } else {
          // User has no workspaces
          setWorkspaceId(null);
          setWorkspaceName(null);
          setWorkspaceLogoUrl(undefined);
          
          // Don't redirect if user is on an invitation link - they should see the invitation page
          if (location.pathname.startsWith("/invite/")) {
            // User is on invitation link, don't redirect - let them see invitation page
            // The invitation page will handle the acceptance flow
            setChecking(false);
            isProcessingAuthRef.current = false;
            hasCompletedInitialAuthCheck.current = true;
            return;
          }
          
          // Only redirect to create workspace if not already there, not on invitation, and not on admin
          if (!hasNavigatedRef.current && location.pathname !== "/create-workspace" && location.pathname !== "/admin" && !location.pathname.startsWith("/invite/")) {
            hasNavigatedRef.current = true;
            navigationTimeoutRef.current = setTimeout(() => {
              navigate("/create-workspace", { replace: true });
              navigationTimeoutRef.current = null;
            }, 100);
          }
          
          setChecking(false);
          isProcessingAuthRef.current = false;
          hasCompletedInitialAuthCheck.current = true;
        }
      }
    });

    // Listen to tab events for cross-tab coordination
    const cleanupTabListener = listenToTabEvents((event) => {
      if (event.type === "logout") {
        // Another tab logged out - clear this tab's context if it matches
        const currentContext = getWorkspaceContext();
        if (currentContext && currentContext.userId === event.userId) {
          // Same user logged out from another tab - clear this tab's context
          clearWorkspaceContext();
        }
      }
    });

    return () => {
      unsubscribe();
      cleanupTabListener();
      // Clear any pending navigation timeouts on cleanup
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, [fetchUserWorkspaces, ensureWorkspaceMembership, navigate]);

  // Listen to current workspace changes to update logo in real-time
  useEffect(() => {
    if (!workspaceId) {
      setWorkspaceLogoUrl(undefined);
      return;
    }

    const unsubscribeWorkspace = onSnapshot(
      doc(db, "workspaces", workspaceId),
      (snapshot) => {
        if (snapshot.exists()) {
          const workspaceData = snapshot.data();
          setWorkspaceLogoUrl(workspaceData?.logoUrl as string | undefined);
          // Also update workspace name if it changed
          if (workspaceData?.name) {
            setWorkspaceName(workspaceData.name as string);
          }
        } else {
          // Workspace was deleted, clear logo
          setWorkspaceLogoUrl(undefined);
        }
      },
      (error) => {
        // Handle errors (e.g., workspace deleted, permission denied)
        console.error("Error listening to workspace changes:", error);
        setWorkspaceLogoUrl(undefined);
      }
    );

    return () => unsubscribeWorkspace();
  }, [workspaceId]);

  /* -------------------- */
  /* Render Logic         */
  /* -------------------- */

  // Check if mobile access should be blocked in "web-only" mode
  const devModeType = getDevModeType();
  const isWebOnlyMode = devModeType === "web-only";
  
  // Block actual mobile devices (not just small browser windows) when in "web-only" mode
  // This allows testing mobile view in desktop browsers by resizing the window
  const isRealMobileDevice = isActualMobileDevice();
  if (isRealMobileDevice && isWebOnlyMode) {
    const handleEnableMobile = () => {
      // Switch to web-mobile mode on this device
      setDevModeType("web-mobile");
      // Reload the page to apply the change
      window.location.reload();
    };

    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default"
        sx={{ p: 3 }}
      >
        <Paper
          elevation={3}
          sx={{ 
            width: "100%", 
            maxWidth: 400, 
            p: 4, 
            borderRadius: 3, 
            textAlign: "center" 
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Box
              component="img"
              src={logo}
              alt="Budget Trace"
              sx={{
                width: 120,
                height: "auto",
              }}
            />
            <Typography variant="h5" fontWeight="bold" color="error">
              Mobile Not Supported
            </Typography>
            <Typography variant="body1" color="text.secondary">
              The app is currently in <strong>"Web Only"</strong> mode, which requires full authentication and does not support mobile devices.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To use the app on mobile, enable <strong>"Web + Mobile"</strong> mode below.
            </Typography>
            <Alert severity="info" sx={{ width: "100%" }}>
              <Typography variant="caption">
                <strong>Note:</strong> Mobile support is only available in "Web + Mobile" mode with bypass authentication enabled.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleEnableMobile}
              sx={{ mt: 2 }}
            >
              Enable Mobile Mode
            </Button>
            <Typography variant="caption" color="text.secondary">
              This will switch to "Web + Mobile" mode and reload the app.
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Allow invitation route to render even while checking auth (user needs to see invitation page to log in)
  const isOnInvitationRoute = location.pathname.startsWith("/invite/");
  // Fix malformed URL /login/activity (or /login/analytics etc.) after redirect login: redirect to correct workspace URL so shell and bottom nav work
  const loginPageMatch = location.pathname.match(/^\/login\/(activity|analytics|accounts|reports|settings)(\/.*)?$/);
  const savedContextForRedirect = getWorkspaceContext();
  if (loginPageMatch && savedContextForRedirect) {
    const page = loginPageMatch[1];
    const slug = workspaceNameToSlug(savedContextForRedirect.workspaceName);
    const rest = loginPageMatch[2] || "";
    return <Navigate to={`/${slug}/${page}${rest}`} replace />;
  }
  // On workspace URL with saved context: show shell + bottom nav immediately so taps (e.g. Analytics) open right away; auth/load continues in background
  const isOnWorkspaceRoutePattern = location.pathname.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|transaction)/);
  const savedContextForEarlyRender = getWorkspaceContext();
  const pathSlug = location.pathname.split("/").filter(Boolean)[0];
  const correctSlug = savedContextForEarlyRender ? workspaceNameToSlug(savedContextForEarlyRender.workspaceName) : "";
  const isOnWorkspaceRouteWithSavedContext = !!(
    isOnWorkspaceRoutePattern &&
    savedContextForEarlyRender &&
    pathSlug &&
    pathSlug === correctSlug
  );
  // Always show loading screen while checking auth (including hard refresh on workspace URLs)
  if (checking && !isOnInvitationRoute) {
    return <InitialLoader />;
  }

  // Check if demo mode is active
  const demoActive = isDemoMode();
  const demoWorkspaceId = demoActive ? getDemoWorkspaceId() : null;
  
  // Public routes (including demo mode routes)
  if (!authUser) {
    // If we're on a workspace page with saved context, preserve it during auth initialization (hard refresh)
    // This prevents showing landing page during hard refresh on workspace pages
    const isOnWorkspaceRoutePattern = location.pathname.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|transaction)/);
    const savedContext = getWorkspaceContext();
    const shouldPreserveWorkspacePage = isOnWorkspaceRoutePattern && savedContext && !hasCompletedInitialAuthCheck.current;
    
    // If demo mode is active, allow workspace routes
    if (demoActive && (workspaceId || demoWorkspaceId)) {
      const effectiveWorkspaceId = workspaceId || demoWorkspaceId;
      return (
        <Routes>
          <Route path="/login" element={<LoginPage onBack={() => navigate("/")} />} />
          <Route path="/invite/:invitationId" element={<InviteAcceptanceWrapper />} />
          <Route path="/admin" element={<RedirectToLogin />} />
          <Route path="/:workspaceSlug/admin" element={<Navigate to="/admin" replace />} />
          <Route path="/:workspaceSlug/*" element={<WorkspaceLayout 
            workspaceId={effectiveWorkspaceId}
            workspaceName={workspaceName || "Demo Workspace"}
            workspaceLogoUrl={workspaceLogoUrl}
            workspaces={[]}
            authUser={null}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            workspaceMenuAnchor={workspaceMenuAnchor}
            setWorkspaceMenuAnchor={setWorkspaceMenuAnchor}
            handleWorkspaceSelect={async () => {}}
            handleLogout={async () => {
              disableDemoMode();
              setWorkspaceId(null);
              setWorkspaceName(null);
              setWorkspaceLogoUrl(undefined);
              navigate("/");
            }}
            createDialogOpen={createDialogOpen}
            setCreateDialogOpen={setCreateDialogOpen}
            newWorkspaceName={newWorkspaceName}
            setNewWorkspaceName={setNewWorkspaceName}
            newWorkspaceCurrency={newWorkspaceCurrency}
            setNewWorkspaceCurrency={setNewWorkspaceCurrency}
            creatingWorkspace={creatingWorkspace}
            handleCreateWorkspace={handleCreateWorkspace}
            activityInitialFilters={activityInitialFilters}
            setActivityInitialFilters={setActivityInitialFilters}
            analyticsKey={analyticsKey}
            setAnalyticsKey={setAnalyticsKey}
            fetchUserWorkspaces={fetchUserWorkspaces}
            snackbarOpen={snackbarOpen}
            setSnackbarOpen={setSnackbarOpen}
            snackbarMessage={snackbarMessage}
            setSnackbarMessage={setSnackbarMessage}
            snackbarElement={snackbarElement}
            setSnackbarElement={setSnackbarElement}
          />} />
          <Route path="*" element={<Navigate to={effectiveWorkspaceId ? `/${workspaceNameToSlug(workspaceName || "workspace")}/activity` : "/"} replace />} />
        </Routes>
      );
    }
    
    // If bypass auth is enabled, allow workspace routes or show workspace entry UI
    if (isBypassAuthEnabled()) {
      const bypassWorkspaceId = getBypassWorkspaceId();
      
      // If we have a bypass workspace ID in URL or localStorage, check if workspace is loaded
      if (bypassWorkspaceId) {
        // If still checking/loading, show loader
        if (checking) {
          console.log("[Bypass Auth Render] Still checking, showing loader");
          return <InitialLoader />;
        }
        
        // If workspace is loaded and matches, show full workspace layout
        if (workspaceId === bypassWorkspaceId && workspaceName) {
          console.log("[Bypass Auth Render] Workspace loaded, showing layout:", workspaceName);
          return (
            <Routes>
              <Route path="/login" element={<LoginPage onBack={() => navigate("/")} />} />
              <Route path="/invite/:invitationId" element={<InviteAcceptanceWrapper />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/:workspaceSlug/admin" element={<Navigate to="/admin" replace />} />
              <Route path="/:workspaceSlug/*" element={<WorkspaceLayout 
                workspaceId={workspaceId}
                workspaceName={workspaceName || "Workspace"}
                workspaceLogoUrl={workspaceLogoUrl}
                workspaces={[]}
                authUser={null}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                workspaceMenuAnchor={workspaceMenuAnchor}
                setWorkspaceMenuAnchor={setWorkspaceMenuAnchor}
                handleWorkspaceSelect={async () => {}}
                handleLogout={async () => {
                  setBypassWorkspaceId(null);
                  setWorkspaceId(null);
                  setWorkspaceName(null);
                  setWorkspaceLogoUrl(undefined);
                  navigate("/");
                }}
                createDialogOpen={createDialogOpen}
                setCreateDialogOpen={setCreateDialogOpen}
                newWorkspaceName={newWorkspaceName}
                setNewWorkspaceName={setNewWorkspaceName}
                newWorkspaceCurrency={newWorkspaceCurrency}
                setNewWorkspaceCurrency={setNewWorkspaceCurrency}
                creatingWorkspace={creatingWorkspace}
                handleCreateWorkspace={handleCreateWorkspace}
                activityInitialFilters={activityInitialFilters}
                setActivityInitialFilters={setActivityInitialFilters}
                analyticsKey={analyticsKey}
                setAnalyticsKey={setAnalyticsKey}
                fetchUserWorkspaces={fetchUserWorkspaces}
                snackbarOpen={snackbarOpen}
                setSnackbarOpen={setSnackbarOpen}
                snackbarMessage={snackbarMessage}
                setSnackbarMessage={setSnackbarMessage}
                snackbarElement={snackbarElement}
                setSnackbarElement={setSnackbarElement}
              />} />
              <Route path="*" element={<Navigate to={workspaceId ? `/${workspaceNameToSlug(workspaceName || "workspace")}/activity` : "/"} replace />} />
            </Routes>
          );
        }
        // Workspace ID exists but workspace is still loading or not loaded yet
        // This happens when bypassWorkspaceId is in URL but workspace hasn't loaded yet
        console.log("[Bypass Auth Render] Workspace ID exists but not loaded yet", {
          bypassWorkspaceId,
          workspaceId,
          workspaceName,
          checking
        });
        return <InitialLoader />;
      } else {
        // Bypass mode without workspace - show workspace entry UI
        return (
          <Routes>
            <Route path="/login" element={<LoginPage onBack={() => navigate("/")} />} />
            <Route path="/invite/:invitationId" element={<InviteAcceptanceWrapper />} />
            <Route path="/admin" element={<RedirectToLogin />} />
            <Route path="/create-workspace" element={
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", p: 3 }}>
                <Box sx={{ maxWidth: 500, width: "100%" }}>
                  <Typography variant="h4" gutterBottom>
                    Development Mode - Bypass Auth
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter a workspace ID to view it without authentication. Creating workspaces still requires login.
                  </Typography>
                  <Box component="form" onSubmit={async (e: React.FormEvent) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);
                    const wsId = formData.get("workspaceId") as string;
                    if (wsId) {
                      setBypassWorkspaceId(wsId);
                      window.location.href = `/?workspaceId=${wsId}`;
                    }
                  }}>
                    <TextField
                      fullWidth
                      name="workspaceId"
                      label="Workspace ID"
                      placeholder="Enter workspace ID..."
                      defaultValue={bypassWorkspaceId || ""}
                      autoFocus
                      type="text"
                      inputProps={{
                        autoComplete: "off",
                        autoCapitalize: "none",
                        autoCorrect: "off",
                        inputMode: "text",
                      }}
                      InputProps={{
                        autoComplete: "off",
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Button type="submit" variant="contained" fullWidth sx={{ mb: 2 }}>
                      Load Workspace
                    </Button>
                    <Button 
                      fullWidth 
                      onClick={() => navigate("/login")}
                      sx={{ mb: 2 }}
                    >
                      Or Login to Create Workspace
                    </Button>
                  </Box>
                </Box>
              </Box>
            } />
            <Route path="*" element={<Navigate to="/create-workspace" replace />} />
          </Routes>
        );
      }
    }
    
    // If we're on a workspace page with saved context during auth initialization:
    // Show workspace shell + bottom nav immediately so user can tap Analytics etc.; auth continues in background
    if (shouldPreserveWorkspacePage) {
      if (isOnWorkspaceRouteWithSavedContext && savedContextForEarlyRender) {
        const ctx = savedContextForEarlyRender;
        return (
          <Routes>
            <Route path="/admin" element={<RedirectToLogin />} />
            <Route path="/:workspaceSlug/admin" element={<Navigate to="/admin" replace />} />
            <Route path="/invite/:invitationId" element={<InviteAcceptanceWrapper />} />
            <Route path="/:workspaceSlug/*" element={<WorkspaceLayout
              workspaceId={ctx.workspaceId}
              workspaceName={ctx.workspaceName}
              workspaceLogoUrl={ctx.workspaceLogoUrl}
              workspaces={[]}
              authUser={null}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              workspaceMenuAnchor={workspaceMenuAnchor}
              setWorkspaceMenuAnchor={setWorkspaceMenuAnchor}
              handleWorkspaceSelect={async () => {}}
              handleLogout={async () => { navigate("/"); }}
              createDialogOpen={createDialogOpen}
              setCreateDialogOpen={setCreateDialogOpen}
              newWorkspaceName={newWorkspaceName}
              setNewWorkspaceName={setNewWorkspaceName}
              newWorkspaceCurrency={newWorkspaceCurrency}
              setNewWorkspaceCurrency={setNewWorkspaceCurrency}
              creatingWorkspace={creatingWorkspace}
              handleCreateWorkspace={handleCreateWorkspace}
              activityInitialFilters={activityInitialFilters}
              setActivityInitialFilters={setActivityInitialFilters}
              analyticsKey={analyticsKey}
              setAnalyticsKey={setAnalyticsKey}
              fetchUserWorkspaces={fetchUserWorkspaces}
              snackbarOpen={snackbarOpen}
              setSnackbarOpen={setSnackbarOpen}
              snackbarMessage={snackbarMessage}
              setSnackbarMessage={setSnackbarMessage}
              snackbarElement={snackbarElement}
              setSnackbarElement={setSnackbarElement}
            />} />
            <Route path="*" element={<Navigate to={`/${workspaceNameToSlug(ctx.workspaceName)}/activity`} replace />} />
          </Routes>
        );
      }
      return <InitialLoader />;
    }
    
    // Regular public routes (when not logged in)
    // /admin must be explicit so it redirects to login instead of matching * (landing)
    if (location.pathname === "/admin") {
      redirectAfterLoginRef.current = "/admin";
    }
    return (
      <Routes>
        <Route path="/admin" element={<RedirectToLogin />} />
        <Route path="/login" element={<LoginPage onBack={() => navigate("/")} />} />
        <Route path="/invite/:invitationId" element={<InviteAcceptanceWrapper />} />
        <Route path="*" element={<LandingPage onLoginClick={() => navigate("/login")} onDemoClick={handleDemoClick} />} />
      </Routes>
    );
  }

  if (switchingWorkspace) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default"
      >
        <Paper
          elevation={0}
          sx={{ width: 360, p: 4, borderRadius: 3, textAlign: "center" }}
        >
          <Stack spacing={3} alignItems="center">
            <Box
              component="img"
              src={logo}
              alt="Budget Trace"
              sx={{
                height: 120,
                width: "auto",
                objectFit: "contain",
              }}
            />
            <CircularProgress />
            <Box>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Changing workspace...
              </Typography>
              {switchingFromWorkspace && switchingToWorkspace && (
                <Typography variant="body2" color="text.secondary">
                  From <strong>{switchingFromWorkspace}</strong> to <strong>{switchingToWorkspace}</strong>
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Authenticated routes: on hard refresh workspaceId may still be null; use saved context so we don't redirect to create-workspace
  const authPathSlug = location.pathname.split("/").filter(Boolean)[0];
  const authSavedContext = getWorkspaceContext();
  const authPathMatchesWorkspace = location.pathname.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|transaction)/);
  const authSlugMatchesContext = authSavedContext && authPathSlug && authPathSlug === workspaceNameToSlug(authSavedContext.workspaceName);
  const effectiveWorkspaceId = workspaceId || (authPathMatchesWorkspace && authSlugMatchesContext ? authSavedContext!.workspaceId : null);
  const effectiveWorkspaceName = workspaceName || (authPathMatchesWorkspace && authSlugMatchesContext ? authSavedContext!.workspaceName : null);
  const effectiveWorkspaceLogoUrl = workspaceLogoUrl ?? (authPathMatchesWorkspace && authSlugMatchesContext ? authSavedContext!.workspaceLogoUrl : undefined);

  // IMPORTANT: Invitation route must come before other routes to prevent redirects
  return (
    <>
      <TermsAcceptanceDialog
        open={showTermsDialog}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
      />
      <Routes>
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/:workspaceSlug/admin" element={<Navigate to="/admin" replace />} />
      <Route path="/invite/:invitationId" element={<InviteAcceptanceWrapper />} />
      <Route path="/create-workspace" element={<CreateWorkspaceWrapper onCreated={setWorkspaceId} />} />
      <Route path="/:workspaceSlug/*" element={<WorkspaceLayout
        workspaceId={effectiveWorkspaceId}
        workspaceName={effectiveWorkspaceName}
        workspaceLogoUrl={effectiveWorkspaceLogoUrl}
        workspaces={workspaces}
        authUser={authUser}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        workspaceMenuAnchor={workspaceMenuAnchor}
        setWorkspaceMenuAnchor={setWorkspaceMenuAnchor}
        handleWorkspaceSelect={handleWorkspaceSelect}
        handleLogout={handleLogout}
        createDialogOpen={createDialogOpen}
        setCreateDialogOpen={setCreateDialogOpen}
        newWorkspaceName={newWorkspaceName}
        setNewWorkspaceName={setNewWorkspaceName}
        newWorkspaceCurrency={newWorkspaceCurrency}
        setNewWorkspaceCurrency={setNewWorkspaceCurrency}
        creatingWorkspace={creatingWorkspace}
        handleCreateWorkspace={handleCreateWorkspace}
        activityInitialFilters={activityInitialFilters}
        setActivityInitialFilters={setActivityInitialFilters}
        analyticsKey={analyticsKey}
        setAnalyticsKey={setAnalyticsKey}
        fetchUserWorkspaces={fetchUserWorkspaces}
        snackbarOpen={snackbarOpen}
        setSnackbarOpen={setSnackbarOpen}
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
        snackbarElement={snackbarElement}
        setSnackbarElement={setSnackbarElement}
        onTestTermsDialog={handleTestTermsDialog}
      />} />
      <Route path="*" element={
        (effectiveWorkspaceId && effectiveWorkspaceName) ? (
          <Navigate to={`/${workspaceNameToSlug(effectiveWorkspaceName)}/activity`} replace />
        ) : (
          <Navigate to="/create-workspace" replace />
        )
      } />
    </Routes>
    </>
  );
}

/* -------------------- */
/* Create Workspace Wrapper */
/* -------------------- */
function CreateWorkspaceWrapper({ onCreated }: { onCreated: (id: string) => void }): ReactElement {
  const navigate = useNavigate();
  
  const handleCreated = async (id: string): Promise<void> => {
    onCreated(id);
    // Get workspace name and navigate
    try {
      const wsSnap = await getDoc(doc(db, "workspaces", id));
      const wsData = wsSnap.data();
      const wsName = wsData?.name ?? "workspace";
      const slug = workspaceNameToSlug(wsName);
      navigate(`/${slug}/activity`);
    } catch (err) {
      console.error("Error navigating after workspace creation:", err);
      navigate("/");
    }
  };
  
  return (
    <CreateWorkspace 
      onCreated={handleCreated} 
    />
  );
}

/* -------------------- */
/* Workspace Layout Component */
/* -------------------- */
interface WorkspaceLayoutProps {
  workspaceId: string | null;
  workspaceName: string | null;
  workspaceLogoUrl: string | undefined;
  workspaces: Workspace[];
  authUser: User | null;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  workspaceMenuAnchor: HTMLElement | null;
  setWorkspaceMenuAnchor: (anchor: HTMLElement | null) => void;
  handleWorkspaceSelect: (id: string) => Promise<void>;
  handleLogout: () => Promise<void>;
  createDialogOpen: boolean;
  setCreateDialogOpen: (open: boolean) => void;
  newWorkspaceName: string;
  setNewWorkspaceName: (name: string) => void;
  newWorkspaceCurrency: typeof COMMON_CURRENCIES[number] | null;
  setNewWorkspaceCurrency: (currency: typeof COMMON_CURRENCIES[number] | null) => void;
  creatingWorkspace: boolean;
  handleCreateWorkspace: () => Promise<void>;
  activityInitialFilters: any;
  setActivityInitialFilters: (filters: any) => void;
  analyticsKey: number;
  setAnalyticsKey: (key: number | ((prev: number) => number)) => void;
  fetchUserWorkspaces: (userId: string) => Promise<Workspace[]>;
  snackbarOpen: boolean;
  setSnackbarOpen: (open: boolean) => void;
  snackbarMessage: string;
  setSnackbarMessage: (message: string) => void;
  snackbarElement: ReactElement | null;
  setSnackbarElement: (element: ReactElement | null) => void;
  onTestTermsDialog?: () => void;
}

// Wrapper component to log when Analytics route renders
function AnalyticsRouteWrapper({
  workspaceId,
  isMobile,
  workspaceSlug,
  setActivityInitialFilters,
  navigate,
  location,
}: {
  workspaceId: string;
  isMobile: boolean;
  workspaceSlug: string | undefined;
  setActivityInitialFilters: (filters: any) => void;
  navigate: (path: string) => void;
  location: { pathname: string };
}): ReactElement {
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {isMobile ? (
        <MobileAnalytics 
          key={`mobile-analytics-${workspaceId}`}
          workspaceId={workspaceId} 
          onNavigateToActivity={(filters) => {
            setActivityInitialFilters(filters);
            navigate(`/${workspaceSlug}/activity`);
            setTimeout(() => setActivityInitialFilters(undefined), 100);
          }}
        />
      ) : (
        <Analytics 
          key={`analytics-${workspaceId}`}
          workspaceId={workspaceId} 
          onNavigateToActivity={(filters) => {
            setActivityInitialFilters(filters);
            navigate(`/${workspaceSlug}/activity`);
            setTimeout(() => setActivityInitialFilters(undefined), 100);
          }}
        />
      )}
    </Box>
  );
}

// Wrapper component for Accounts route (mobile vs desktop)
function AccountsRouteWrapper({
  workspaceId,
  isMobile,
}: {
  workspaceId: string;
  isMobile: boolean;
}): ReactElement {
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {isMobile ? (
        <MobileAccounts 
          key={`mobile-accounts-${workspaceId}`}
          workspaceId={workspaceId} 
        />
      ) : (
        <Accounts 
          key={`accounts-${workspaceId}`}
          workspaceId={workspaceId} 
        />
      )}
    </Box>
  );
}

/** Renders workspace content based on pathname so navigation works on mobile (inner Routes matching can be inconsistent). */
function WorkspaceContentByPath({
  pathname,
  workspaceSlug,
  workspaceId,
  isMobile,
  activityInitialFilters,
  setActivityInitialFilters,
  navigate,
  onTestTermsDialog,
  onOpenWorkspaceMenu,
  onOpenCreateWorkspaceDialog,
  onLogout,
}: {
  pathname: string;
  workspaceSlug: string | undefined;
  workspaceId: string;
  isMobile: boolean;
  activityInitialFilters: any;
  setActivityInitialFilters: (filters: any) => void;
  navigate: (path: string) => void;
  onTestTermsDialog?: () => void;
  onOpenWorkspaceMenu?: (anchorEl: HTMLElement | null) => void;
  onOpenCreateWorkspaceDialog?: () => void;
  onLogout?: () => Promise<void>;
}): ReactElement {
  const segments = pathname.split("/").filter(Boolean);
  const page = segments[1];
  const sub1 = segments[2];
  const sub2 = segments[3];

  if (!workspaceSlug) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Index or unknown -> redirect to activity
  if (!page || (page !== "activity" && page !== "analytics" && page !== "accounts" && page !== "reports" && page !== "settings" &&
      page !== "create-transaction" && page !== "edit-transaction" && page !== "create-account" && page !== "edit-account" &&
      page !== "transaction")) {
    return <Navigate to={`/${workspaceSlug}/activity`} replace />;
  }

  if (page === "activity") {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Activity workspaceId={workspaceId} initialFilters={activityInitialFilters} />
      </Box>
    );
  }

  // Shared transaction view URL: /:workspaceSlug/transaction/:transactionId
  if (page === "transaction" && sub1) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Activity workspaceId={workspaceId} initialFilters={activityInitialFilters} initialViewTransactionId={sub1} />
      </Box>
    );
  }

  if (page === "analytics") {
    if (sub1 && sub2) {
      return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <CategoryGroupDetail
            workspaceId={workspaceId}
            type={sub1}
            name={sub2}
            onNavigateToActivity={(filters) => {
              setActivityInitialFilters(filters);
              navigate(`/${workspaceSlug}/activity`);
              setTimeout(() => setActivityInitialFilters(undefined), 100);
            }}
          />
        </Box>
      );
    }
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {isMobile ? (
          <MobileAnalytics
            key={`mobile-analytics-${workspaceId}`}
            workspaceId={workspaceId}
            onNavigateToActivity={(filters) => {
              setActivityInitialFilters(filters);
              navigate(`/${workspaceSlug}/activity`);
              setTimeout(() => setActivityInitialFilters(undefined), 100);
            }}
          />
        ) : (
          <Analytics
            key={`analytics-${workspaceId}`}
            workspaceId={workspaceId}
            onNavigateToActivity={(filters) => {
              setActivityInitialFilters(filters);
              navigate(`/${workspaceSlug}/activity`);
              setTimeout(() => setActivityInitialFilters(undefined), 100);
            }}
          />
        )}
      </Box>
    );
  }

  if (page === "accounts") {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {isMobile ? (
          <MobileAccounts key={`mobile-accounts-${workspaceId}`} workspaceId={workspaceId} />
        ) : (
          <Accounts key={`accounts-${workspaceId}`} workspaceId={workspaceId} />
        )}
      </Box>
    );
  }

  if (page === "reports") {
    return (
      <Box sx={{ p: 3, pt: 10 }}>
        <Typography variant="h4" gutterBottom>
          Reports
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Reports will be available here.
        </Typography>
      </Box>
    );
  }

  if (page === "create-transaction" && sub1) {
    return <CreateTransactionMobile workspaceId={workspaceId} />;
  }
  if (page === "edit-transaction" && sub1 && sub2) {
    return <CreateTransactionMobile workspaceId={workspaceId} />;
  }
  if (page === "create-account") {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <MobileAccountForm workspaceId={workspaceId} />
      </Box>
    );
  }
  if (page === "edit-account" && sub1) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <MobileAccountForm workspaceId={workspaceId} />
      </Box>
    );
  }

  if (page === "settings") {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Settings
          workspaceId={workspaceId}
          onBack={() => navigate(`/${workspaceSlug}/activity`)}
          onTestTermsDialog={onTestTermsDialog}
          onOpenWorkspaceMenu={onOpenWorkspaceMenu}
          onOpenCreateWorkspaceDialog={onOpenCreateWorkspaceDialog}
          onLogout={onLogout}
        />
      </Box>
    );
  }

  return <Navigate to={`/${workspaceSlug}/activity`} replace />;
}

function WorkspaceLayout(props: WorkspaceLayoutProps): ReactElement {
  const { workspaceId, workspaceName, workspaceLogoUrl, workspaces, authUser, sidebarOpen, setSidebarOpen, 
          workspaceMenuAnchor, setWorkspaceMenuAnchor, handleWorkspaceSelect, handleLogout,
          createDialogOpen, setCreateDialogOpen, newWorkspaceName, setNewWorkspaceName,
          newWorkspaceCurrency, setNewWorkspaceCurrency, creatingWorkspace, handleCreateWorkspace,
          activityInitialFilters, setActivityInitialFilters, analyticsKey, setAnalyticsKey,
          snackbarOpen, setSnackbarOpen, snackbarMessage, setSnackbarMessage,
          snackbarElement, setSnackbarElement, onTestTermsDialog } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Track location changes (dev-only logs to avoid slowing first navigation after hard refresh)
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);
  const [workspaceLogoHover, setWorkspaceLogoHover] = useState(false);
  const [arrowHover, setArrowHover] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevSidebarOpenRef = useRef(sidebarOpen);
  const workspaceLogoRef = useRef<HTMLElement | null>(null);
  const workspaceHeaderRef = useRef<HTMLElement | null>(null);

  // Handle sidebar close with transition tracking
  const handleSidebarClose = () => {
    setIsTransitioning(true);
    setWorkspaceLogoHover(false); // Clear hover to prevent arrow from appearing
    setSidebarOpen(false);
    // Reset transition state after animation completes
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Track sidebar state changes to handle transitions
  useEffect(() => {
    if (prevSidebarOpenRef.current && !sidebarOpen) {
      // Sidebar is closing
      setIsTransitioning(true);
      setWorkspaceLogoHover(false); // Clear hover to prevent arrow from appearing
      setTimeout(() => setIsTransitioning(false), 300);
    }
    prevSidebarOpenRef.current = sidebarOpen;
  }, [sidebarOpen]);

  // Determine current page from URL
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentPage = pathParts.length > 1 ? pathParts[1] : '';
  const isActivityPage = !currentPage || currentPage === 'activity' || currentPage === 'create-transaction' || currentPage === 'edit-transaction';
  const isAccountsPage = currentPage === 'accounts' || currentPage === 'create-account' || currentPage === 'edit-account';
  const isSettingsPage = currentPage === 'settings';
  const isTransactionPage = currentPage === 'create-transaction' || currentPage === 'edit-transaction';
  const isAccountFormPage = currentPage === 'create-account' || currentPage === 'edit-account';
  const settingsSubPage = pathParts.length > 2 ? pathParts[2] : 'workspace';

  // Bottom nav: respond immediately so tab switch is not blocked by graph animation or loading
  const handleBottomNavigation = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    if (!workspaceSlug) return;
    const targetPath = (() => {
      switch (newValue) {
        case 0: return `/${workspaceSlug}/activity`;
        case 1: return `/${workspaceSlug}/analytics`;
        case 2: return `/${workspaceSlug}/accounts`;
        case 3: return `/${workspaceSlug}/reports`;
        case 4: return `/${workspaceSlug}/settings`;
        default: return null;
      }
    })();
    if (!targetPath || location.pathname === targetPath) return;
    // Use startTransition so first paint isn't blocked by heavy page mount (faster perceived navigation after hard refresh)
    startTransition(() => {
      navigate(targetPath, { replace: true });
    });
  }, [workspaceSlug, location.pathname, navigate]);

  // Remove pageLoading - React Router handles navigation smoothly without needing loading state
  // This prevents page components from restarting on navigation

  // If no workspace ID: on workspace URL (e.g. hard refresh) show loading so we don't redirect to create-workspace; otherwise redirect
  if (!workspaceId) {
    const onWorkspacePagePath = location.pathname.match(/^\/[^/]+\/(activity|analytics|accounts|reports|settings|transaction)/);
    if (workspaceSlug && onWorkspacePagePath) {
      // Stay on URL and show loading (auth/context may still be restoring)
      return (
        <Box sx={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      );
    }
    if (location.pathname !== "/create-workspace" && !location.pathname.startsWith("/invite/")) {
      return <Navigate to="/create-workspace" replace />;
    }
    return (
      <Box sx={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const drawerWidth = 252;
  const collapsedWidth = 65; // Increased width to ensure icons and logos are fully visible

  return (
    <Box sx={{ display: "flex", height: "100vh", width: "100vw", maxWidth: "100vw", bgcolor: "background.default", overflow: "hidden", position: "relative" }}>
      <ThemeSync workspaceId={workspaceId} />
      {/* Preload Analytics data when on other pages so Analytics opens instantly */}
      {currentPage !== "analytics" ? <WorkspaceDataPreloader workspaceId={workspaceId} /> : null}
      {/* Preload Activity data when on other pages so Activity opens instantly (no 2s loading lines) */}
      {!isActivityPage ? <ActivityDataPreloader workspaceId={workspaceId} /> : null}
      {/* Preload Accounts data when on other pages so Accounts opens instantly after refresh or navigation */}
      {!isAccountsPage ? <AccountsDataPreloader workspaceId={workspaceId} /> : null}
      {/* Preload Settings data (also when on Settings) so all sections open instantly when switching between them */}
      <SettingsDataPreloader workspaceId={workspaceId} />

      {/* Single Expandable Sidebar - Hidden on mobile */}
      {!isSettingsPage && !isMobile && (
        <Box
          sx={{
            width: sidebarOpen ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            borderRight: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            overflowX: "hidden",
            bgcolor: "background.paper",
            position: "relative",
            zIndex: 1000,
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            willChange: "width",
          }}
        >
          <Box sx={{ p: 0.5, pt: 2, display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0, overflowX: "hidden", width: "100%" }}>
            {/* Workspace Logo Section */}
            <Box 
              ref={workspaceHeaderRef}
              sx={{ 
                mb: 2,
                height: 48,
                display: "flex",
                alignItems: "center",
                gap: sidebarOpen ? 1.5 : 0,
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
              }}
              onMouseEnter={() => !sidebarOpen && !isTransitioning && setWorkspaceLogoHover(true)}
              onMouseLeave={() => setWorkspaceLogoHover(false)}
            >
              {!creatingWorkspace && workspaceId && (
                <>
                  {/* Workspace Logo - Static position, aligned with icon centers */}
                  <Box
                    ref={workspaceLogoRef}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                      if (sidebarOpen && !creatingWorkspace && workspaceHeaderRef.current) {
                        setWorkspaceMenuAnchor(workspaceHeaderRef.current);
                      } else if (!sidebarOpen) {
                        setSidebarOpen(true);
                      }
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1rem",
                      color: "primary.contrastText",
                      overflow: "hidden",
                      position: "relative",
                      zIndex: 10,
                      cursor: "pointer",
                      flexShrink: 0,
                      transition: "none",
                      // Logo position: Aligned with icon centers
                      // Icon center: 16px (container) + 8px (ListItemButton) + 4px (icon margin) + 16px (half icon) = 44px
                      // Logo center should be at 44px, logo start at 24px, so marginLeft: 24 - 16 = 8px
                      marginLeft: "8px", // Aligned with navigation icon centers
                    }}
                  >
                    {workspaceLogoUrl ? (
                      <Box
                        component="img"
                        src={workspaceLogoUrl}
                        alt={workspaceName || "Workspace"}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.style.display = "none";
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            const fallback = parent.querySelector(".workspace-fallback") as HTMLElement;
                            if (fallback) fallback.style.display = "flex";
                          }
                        }}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : null}
                    <Box
                      className="workspace-fallback"
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: workspaceLogoUrl ? "none" : "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: workspaceLogoUrl ? "absolute" : "static",
                        top: 0,
                        left: 0,
                      }}
                    >
                      {workspaceName?.[0]?.toUpperCase() || "W"}
                    </Box>
                  </Box>

                  {/* Workspace Name Text - Appears when expanded */}
                  {sidebarOpen && (
                    <Box
                      onClick={() => {
                        if (!creatingWorkspace && workspaceHeaderRef.current) {
                          setWorkspaceMenuAnchor(workspaceHeaderRef.current);
                        }
                      }}
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                        maxWidth: "100%",
                        overflowX: "hidden",
                        opacity: sidebarOpen ? 1 : 0,
                        transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: creatingWorkspace ? "default" : "pointer",
                        borderRadius: 1,
                        px: 0.5,
                        py: 0.25,
                        "&:hover": {
                          bgcolor: creatingWorkspace ? "transparent" : "action.hover",
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          overflowX: "hidden",
                          textOverflow: "ellipsis",
                          cursor: creatingWorkspace ? "default" : "pointer",
                          flexGrow: 1,
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      >
                        {creatingWorkspace ? "Creating workspace..." : (workspaceName || "Select Workspace")}
                      </Typography>
                      {!creatingWorkspace && (
                        <Box 
                          component="span" 
                          sx={{ 
                            flexShrink: 0,
                            fontSize: "0.75rem",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          ▼
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Collapse Button - Appears when expanded */}
                  {sidebarOpen && (
                    <Box
                      onMouseEnter={() => setArrowHover(true)}
                      onMouseLeave={() => setArrowHover(false)}
                      sx={{
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {arrowHover && (
                        <Tooltip
                          title="Collapse sidebar"
                          placement="right"
                          arrow
                        >
                          <IconButton
                            onClick={handleSidebarClose}
                            size="small"
                            sx={{
                              minWidth: 24,
                              width: 24,
                              height: 24,
                              color: "text.secondary",
                              "&:hover": {
                                bgcolor: "action.hover",
                                color: "text.primary",
                              },
                            }}
                          >
                            <ChevronLeftIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}

                  {/* Hover arrow indicator - When collapsed */}
                  {workspaceLogoHover && !sidebarOpen && !isTransitioning && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: collapsedWidth,
                        top: "50%",
                        transform: "translateY(-50%)",
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        boxShadow: 2,
                        p: 0.5,
                        display: "flex",
                        alignItems: "center",
                        zIndex: 12,
                        ml: 0.5,
                      }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </Box>
                  )}
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Sidebar Menu Items - Icons static, text appears when expanded */}
            <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden", position: "relative", width: "100%", maxWidth: "100%" }}>
              <List sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                {[
                  { icon: <ReceiptIcon fontSize="medium" />, text: "Activity", route: "activity", selected: isActivityPage, tooltip: "Activity" },
                  { icon: <AnalyticsIcon fontSize="medium" />, text: "Analytics", route: "analytics", selected: currentPage === "analytics", tooltip: "Analytics" },
                  { icon: <AccountBalanceWalletIcon fontSize="medium" />, text: "Accounts", route: "accounts", selected: currentPage === "accounts", tooltip: "Accounts" },
                  { icon: <DescriptionIcon fontSize="medium" />, text: "Reports", route: "reports", selected: currentPage === "reports", tooltip: "Reports" },
                  { icon: <SettingsIcon fontSize="medium" />, text: "Settings", route: "settings", selected: isSettingsPage, tooltip: "Settings" },
                ].map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
                    <Tooltip title={sidebarOpen ? "" : item.tooltip} placement="right">
                      <ListItemButton
                        component={Box}
                        selected={item.selected}
                        onClick={() => {
                          console.log(`[Sidebar Navigation] Clicked on: ${item.text}`, {
                            route: item.route,
                            workspaceSlug,
                            currentPath: location.pathname,
                            timestamp: new Date().toISOString()
                          });
                          
                          // Ensure workspaceSlug is available before navigating
                          if (!workspaceSlug) {
                            console.warn("[Sidebar Navigation] ❌ workspaceSlug not available, cannot navigate", {
                              item: item.text,
                              route: item.route
                            });
                            return;
                          }
                          
                          // Build path dynamically to ensure workspaceSlug is current
                          const targetPath = `/${workspaceSlug}/${item.route}`;
                          
                          if (process.env.NODE_ENV === "development") {
                            console.log(`[Sidebar Navigation] Navigating to: ${targetPath}`);
                          }
                          startTransition(() => {
                            navigate(targetPath, { replace: true });
                          });
                        }}
                        sx={{
                          position: "relative",
                          minHeight: 48,
                          px: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: sidebarOpen ? 1.5 : 0,
                          textDecoration: "none",
                          cursor: "pointer",
                          bgcolor: "transparent",
                          width: "100%",
                          maxWidth: "100%",
                          overflowX: "hidden",
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                          "&.Mui-selected": {
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            "&:hover": {
                              bgcolor: "primary.dark",
                            },
                          },
                        }}
                      >
                        {/* Icon Container - Fixed position, stays static like workspace logo */}
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            minWidth: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: item.selected ? "inherit" : "text.secondary",
                            flexShrink: 0,
                            transition: "none",
                            position: "relative",
                            zIndex: 10,
                            // Fixed static position, independent of collapsedWidth
                            // Aligned with logo: icon center should match logo center
                            // Icon: 16px (container) + 8px (ListItemButton) + marginLeft + 20px (half icon) = logo center
                            marginLeft: "4px", // Fixed position - icons stay aligned with logo
                          }}
                        >
                          {item.icon}
                        </Box>

                        {/* Text Label - Appears when expanded, positioned to the right of icon */}
                        {sidebarOpen && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: item.selected ? "bold" : 400,
                              color: "inherit",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              opacity: sidebarOpen ? 1 : 0,
                              transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              flexGrow: 1,
                              minWidth: 0,
                              maxWidth: "100%",
                            }}
                          >
                            {item.text}
                          </Typography>
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* User Information Section */}
            {authUser && (
              <Box sx={{ 
                mb: 1.5, 
                display: "flex",
                alignItems: "center",
                gap: sidebarOpen ? 1.5 : 0,
                minHeight: 48,
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
              }}>
                {/* User Avatar - Static position, aligned with icon centers */}
                <Box
                  onClick={() => {
                    if (!sidebarOpen) {
                      setSidebarOpen(true);
                    }
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1rem",
                    color: "primary.contrastText",
                    overflow: "hidden",
                    position: "relative",
                    zIndex: 10,
                    flexShrink: 0,
                    transition: "none",
                    cursor: "pointer",
                    // Avatar position: Aligned with icon centers (same as workspace logo)
                    marginLeft: "8px", // Aligned with navigation icon centers
                    "&:hover": {
                      opacity: 0.9,
                    },
                  }}
                >
                  {authUser.photoURL ? (
                    <Box
                      component="img"
                      src={authUser.photoURL}
                      alt={authUser?.displayName || "User"}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.style.display = "none";
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallback = parent.querySelector(".user-fallback") as HTMLElement;
                          if (fallback) fallback.style.display = "flex";
                        }
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : null}
                  <Box
                    className="user-fallback"
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: authUser.photoURL ? "none" : "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: authUser.photoURL ? "absolute" : "static",
                      top: 0,
                      left: 0,
                    }}
                  >
                    {authUser?.displayName?.[0]?.toUpperCase() || authUser?.email?.[0]?.toUpperCase() || "D"}
                  </Box>
                </Box>

                {/* User Info Text - Appears when expanded */}
                {sidebarOpen && (
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      minWidth: 0,
                      maxWidth: "100%",
                      overflowX: "hidden",
                      opacity: sidebarOpen ? 1 : 0,
                      transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium" noWrap sx={{ overflowX: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                      {authUser?.displayName || (isBypassAuthEnabled() ? "Bypass-User" : "Demo User")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ overflowX: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                      {authUser?.email || (isBypassAuthEnabled() ? "Bypass Mode" : "Demo Mode")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Workspace menu: Dialog popup on mobile, Menu on desktop */}
      {isMobile ? (
        <Dialog
          open={Boolean(workspaceMenuAnchor)}
          onClose={() => setWorkspaceMenuAnchor(null)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: "85vh",
              mx: 1,
            },
          }}
        >
          <DialogTitle sx={{ pb: 0 }}>Switch workspace</DialogTitle>
          <DialogContent sx={{ pt: 1.5, px: 0 }}>
            <List disablePadding>
              {workspaces.filter(ws => ws.id !== workspaceId).map((ws) => (
                <ListItem key={ws.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleWorkspaceSelect(ws.id)}
                    sx={{ py: 1.5, px: 2, display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1rem",
                        color: "primary.contrastText",
                        mr: 2,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      {ws.logoUrl ? (
                        <Box component="img" src={ws.logoUrl} alt={ws.name || "Workspace"} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span>{ws.name?.[0]?.toUpperCase() || "W"}</span>
                      )}
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{ws.name}</Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setWorkspaceMenuAnchor(null);
                    setCreateDialogOpen(true);
                  }}
                  sx={{ py: 1.5, px: 2, color: "primary.main" }}
                >
                  <Box sx={{ width: 40, display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                    <AddIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="body1" fontWeight={500}>Create new workspace</Typography>
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setWorkspaceMenuAnchor(null);
                    handleLogout();
                  }}
                  sx={{ py: 1.5, px: 2, color: "error.main" }}
                >
                  <Box sx={{ width: 40, display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                    <LogoutIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="body1" fontWeight={500}>Logout</Typography>
                </ListItemButton>
              </ListItem>
            </List>
          </DialogContent>
        </Dialog>
      ) : (
        <Menu
          anchorEl={workspaceMenuAnchor}
          open={Boolean(workspaceMenuAnchor)}
          onClose={() => setWorkspaceMenuAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: drawerWidth - 32,
              display: "flex",
              flexDirection: "column",
              maxHeight: 400,
              mt: 0,
            },
          }}
          disableAutoFocusItem
        >
        {workspaces.filter(ws => ws.id !== workspaceId).length > 0 && (
          <Box
            sx={{
              overflowY: workspaces.filter(ws => ws.id !== workspaceId).length > 5 ? "auto" : "visible",
              maxHeight: workspaces.filter(ws => ws.id !== workspaceId).length > 5 ? "calc(400px - 120px)" : "none",
              flexShrink: 0,
            }}
          >
            {workspaces
              .filter(ws => ws.id !== workspaceId)
              .map((ws) => (
                <MenuItem
                  key={ws.id}
                  onClick={() => handleWorkspaceSelect(ws.id)}
                  sx={{
                    "&:hover": { bgcolor: "action.hover" },
                    transition: "background-color 0.2s ease",
                    pl: 1,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 0.75,
                      bgcolor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      color: "primary.contrastText",
                      mr: 3,
                      overflow: "hidden",
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    {ws.logoUrl ? (
                      <Box
                        component="img"
                        src={ws.logoUrl}
                        alt={ws.name || "Workspace"}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.style.display = "none";
                          const parent = e.currentTarget.parentElement;
                          if (parent) {
                            const fallback = parent.querySelector(".workspace-menu-fallback") as HTMLElement;
                            if (fallback) fallback.style.display = "flex";
                          }
                        }}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : null}
                    <Box
                      className="workspace-menu-fallback"
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: ws.logoUrl ? "none" : "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: ws.logoUrl ? "absolute" : "static",
                        top: 0,
                        left: 0,
                      }}
                    >
                      {ws.name?.[0]?.toUpperCase() || "W"}
                    </Box>
                  </Box>
                  <Typography variant="body2" noWrap>{ws.name}</Typography>
                </MenuItem>
              ))}
          </Box>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setWorkspaceMenuAnchor(null);
            setCreateDialogOpen(true);
          }}
          sx={{
            color: "primary.main",
            flexShrink: 0,
            pl: 1,
            minHeight: 48,
            "&:hover": { bgcolor: "action.hover" },
            transition: "background-color 0.2s ease",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", mr: 3, flexShrink: 0 }}>
            <AddIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">Create New Workspace</Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setWorkspaceMenuAnchor(null);
            handleLogout();
          }}
          sx={{
            color: "error.main",
            flexShrink: 0,
            pl: 1,
            minHeight: 48,
            "&:hover": { bgcolor: "action.hover" },
            transition: "background-color 0.2s ease",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", mr: 3, flexShrink: 0 }}>
            <LogoutIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>
      )}

      {/* Main Content Area - pathname-based so mobile bottom nav always shows the right page */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          pt: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          overflowX: "hidden",
          pb: isMobile && !isSettingsPage ? "56px" : 0, // Bottom nav only on non-Settings; Settings uses full height (no nav)
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <WorkspaceContentByPath
          pathname={location.pathname}
          workspaceSlug={workspaceSlug}
          workspaceId={workspaceId!}
          isMobile={isMobile}
          activityInitialFilters={activityInitialFilters}
          setActivityInitialFilters={setActivityInitialFilters}
          navigate={navigate}
          onTestTermsDialog={onTestTermsDialog}
          onOpenWorkspaceMenu={setWorkspaceMenuAnchor}
          onOpenCreateWorkspaceDialog={() => setCreateDialogOpen(true)}
          onLogout={handleLogout}
        />
        </Box>
      </Box>

      {/* Create Workspace Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setNewWorkspaceName("");
        }}
        maxWidth="xs"
        PaperProps={{
          sx: { 
            borderRadius: 1,
            width: "400px",
            maxWidth: "90vw",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>Create New Workspace</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Workspace Name"
              fullWidth
              variant="outlined"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              placeholder="e.g. Personal Finance"
              disabled={creatingWorkspace}
              onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && newWorkspaceName.trim() && newWorkspaceCurrency && !creatingWorkspace) {
                  handleCreateWorkspace();
                }
              }}
            />
            <Autocomplete
              fullWidth
              options={COMMON_CURRENCIES}
              value={newWorkspaceCurrency}
              onChange={(_, newValue) => setNewWorkspaceCurrency(newValue)}
              getOptionLabel={(option) => `${option.code} - ${option.name} (${option.symbol})`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              filterOptions={(options, { inputValue }) => {
                const searchValue = inputValue.toLowerCase();
                return options.filter(
                  (option) =>
                    option.code.toLowerCase().includes(searchValue) ||
                    option.name.toLowerCase().includes(searchValue) ||
                    option.symbol.toLowerCase().includes(searchValue)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Default Currency"
                  placeholder="Select default currency"
                  required
                  disabled={creatingWorkspace}
                />
              )}
              disabled={creatingWorkspace}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setNewWorkspaceName("");
              setNewWorkspaceCurrency(null);
            }}
            disabled={creatingWorkspace}
            variant="outlined"
            sx={{ flex: 1, py: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateWorkspace}
            variant="contained"
            disabled={creatingWorkspace || !newWorkspaceName.trim() || !newWorkspaceCurrency}
            sx={{ flex: 1, py: 1.5 }}
          >
            {creatingWorkspace ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bottom Navigation Bar - Mobile Only; highest priority so taps respond even during graph animation */}
      {isMobile && !isSettingsPage && !isTransactionPage && !isAccountFormPage && (
        <BottomNavigation
          value={isActivityPage ? 0 : currentPage === "analytics" ? 1 : currentPage === "accounts" ? 2 : currentPage === "reports" ? 3 : 4}
          onChange={handleBottomNavigation}
          showLabels
          sx={{
            position: "fixed",
            bottom: "20px",
            left: 0,
            right: 0,
            zIndex: 9999,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            height: "56px",
            pointerEvents: "auto",
            touchAction: "manipulation",
            isolation: "isolate",
            "& .MuiBottomNavigationAction-root": {
              pointerEvents: "auto",
            },
          }}
        >
          <BottomNavigationAction
            label="Activity"
            icon={<ReceiptIcon />}
          />
          <BottomNavigationAction
            label="Analytics"
            icon={<AnalyticsIcon />}
          />
          <BottomNavigationAction
            label="Accounts"
            icon={<AccountBalanceWalletIcon />}
          />
          <BottomNavigationAction
            label="Reports"
            icon={<DescriptionIcon />}
          />
          <BottomNavigationAction
            label="Settings"
            icon={<SettingsIcon />}
          />
        </BottomNavigation>
      )}

      {/* Dev workspace indicator (bottom bar) - only when bypass auth enabled */}
      <DevWorkspaceIndicator isMobile={isMobile} isSettingsPage={isSettingsPage} isTransactionPage={isTransactionPage} isAccountFormPage={isAccountFormPage} />

      {/* Snackbar for demo mode alerts */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarElement(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: isMobile ? "92px" : "auto" }} // Adjust snackbar position on mobile to be above bottom nav (56px nav + 20px offset + 16px gap)
      >
        <Alert onClose={() => {
          setSnackbarOpen(false);
          setSnackbarElement(null);
        }} severity="info" sx={{ width: "100%" }}>
          {snackbarElement || snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default App;
